/**
 * Port of lib/traits.py.
 *
 * Deterministic trait -> Ascendant scorer.
 *
 * The single biggest unknown in a birth time is the rising sign (it cycles through all
 * twelve across a day). Life-event dasha timing fixes *when in the cycle* a chart sits,
 * but a few dated events rarely carry enough information to pin the rising sign on their
 * own. Observable physical/temperament traits are a nearly independent source of
 * information that maps directly onto the Ascendant (and planets in the Ascendant), so
 * they collapse a "morning or evening, no time" range to a window. This is exactly the
 * rectification use of physical features described in source_learning/Vol_06.
 *
 * All scoring here is fully deterministic: the user answers a fixed questionnaire, each
 * answer carries per-sign weights drawn from Vol_06 sections 2.2 (rising-sign appearance &
 * temperament) and 2.3 (planet-in-Ascendant modifiers). No AI, no free text.
 */

import { todayLocal, tryParseISODate, diffDays, type PlainDate } from "./datetime";
import { pythonRound } from "./pyutil";

// Sign indices: 0 Aries, 1 Taurus, 2 Gemini, 3 Cancer, 4 Leo, 5 Virgo,
//               6 Libra, 7 Scorpio, 8 Sagittarius, 9 Capricorn, 10 Aquarius, 11 Pisces
export const ARIES = 0,
  TAURUS = 1,
  GEMINI = 2,
  CANCER = 3,
  LEO = 4,
  VIRGO = 5;
export const LIBRA = 6,
  SCORPIO = 7,
  SAGITTARIUS = 8,
  CAPRICORN = 9,
  AQUARIUS = 10,
  PISCES = 11;
export const SIGN_NAMES: string[] = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

const _GRAHAS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"] as const;

// ── Local mirrors of two tiny astro pieces ───────────────────────────────────────────────
// Python imports SIGN_RULERS from astro_calculations/astrology/_constants.py and
// get_divisional_position from astro_calculations/astrology/_helpers.py. This cluster must
// not import from src/astro (ported concurrently by another agent), so the two pieces are
// mirrored verbatim here: the 12-entry rulership table, and ONLY the division==9 (Navamsa)
// branch of get_divisional_position (the single division traits.py ever requests).

/** Mirror of astro_calculations/astrology/_constants.py SIGN_RULERS. */
const SIGN_RULERS: Record<number, string> = {
  0: "mars", 1: "venus", 2: "mercury", 3: "moon", 4: "sun", 5: "mercury",
  6: "venus", 7: "mars", 8: "jupiter", 9: "saturn", 10: "saturn", 11: "jupiter",
};

/**
 * The D9 (Navamsa) sign for a longitude — the division==9 branch of the canonical
 * get_divisional_position in astro_calculations/astrology/transit_data.py, returning
 * only the divisional sign (traits.py uses [0] of the (sign, degrees) tuple).
 */
function _navamsa_sign(lon: number): number {
  const sign = Math.trunc(lon / 30); // 0=Aries, 1=Taurus, etc.
  const sign_pos = lon % 30; // Position within the sign (0-30 degrees)
  const part_size = 30.0 / 9;
  const part = Math.max(0, Math.min(Math.trunc(sign_pos / part_size), 8));
  // Mapping depends on sign's element (triplicity)
  const sign_element = sign % 4; // 0,4,8=Fire; 1,5,9=Earth; 2,6,10=Air; 3,7,11=Water
  let divisional_sign: number;
  if (sign_element === 0) {
    divisional_sign = (0 + part) % 12; // Fire signs: start from Aries
  } else if (sign_element === 1) {
    divisional_sign = (9 + part) % 12; // Earth signs: start from Capricorn
  } else if (sign_element === 2) {
    divisional_sign = (6 + part) % 12; // Air signs: start from Libra
  } else {
    divisional_sign = (3 + part) % 12; // Water signs: start from Cancer
  }
  return divisional_sign;
}

// ── Appearance MODIFIERS (Vol_06 §2.2/§2.3 — appearance is read from the chart, not the sign
// alone). These make the physical scorer modifier-based rather than a sign-only checklist, so a
// validated chart whose body doesn't fit the rising-sign cliché (e.g. a lean, angular Cancer
// lagna) is NOT scored near zero — the lagna nakshatra & its lord, the lagna lord's condition,
// and the planets in/aspecting the 1st all colour the body and buffer a weak sign baseline.

// Vimshottari nakshatra lords from Ashwini (idx 0); the 27 nakshatras cycle this 9-lord set 3×.
const _NAKSHATRA_VIM_LORDS = ["ketu", "venus", "sun", "moon", "mars", "rahu", "jupiter", "saturn", "mercury"] as const;
const _NAKSHATRA_SPAN = 360 / 27;

/** The Vimshottari lord of the nakshatra a longitude falls in (the LAGNA nakshatra lord). */
function _nakshatra_lord(lon: number): string {
  const idx = Math.floor((((lon % 360) + 360) % 360) / _NAKSHATRA_SPAN); // 0..26
  return _NAKSHATRA_VIM_LORDS[idx % 9];
}

// Whole-sign graha drishti onto a house, as OFFSETS from the planet's sign (0 = same sign, 6 =
// 7th). Every graha aspects the 7th; Mars adds 4th/8th, Jupiter 5th/9th, Saturn 3rd/10th; the
// nodes aspect the 7th only (matching the engine's drishti rule, RULES_TRACE #27).
const _ASPECT_OFFSETS: Record<string, Set<number>> = {
  sun: new Set([6]), moon: new Set([6]), mercury: new Set([6]), venus: new Set([6]),
  mars: new Set([6, 3, 7]), jupiter: new Set([6, 4, 8]), saturn: new Set([6, 2, 9]),
  rahu: new Set([6]), ketu: new Set([6]),
};

/** Grahas NOT occupying the lagna whose drishti falls on the 1st house. */
function _planets_aspecting_first(natal_chart: any, asc_sign: number): string[] {
  const out: string[] = [];
  for (const graha of _GRAHAS) {
    const p = natal_chart?.planets?.[graha];
    if (p === null || p === undefined) continue;
    const sign = Math.trunc(Number(p.sign));
    if (sign === asc_sign) continue; // occupants are the PRIMARY signature, scored separately
    const off = (((asc_sign - sign) % 12) + 12) % 12;
    if (_ASPECT_OFFSETS[graha]?.has(off)) out.push(graha);
  }
  return out;
}

/**
 * The lagna lord's CONDITION as a 0..1 reliability (how cleanly the body can express its
 * signature), derived from its ShadbalaLite total RELATIVE to the chart's own planets — scale
 * free, so it works on any chart that carries shadbala. null when shadbala is absent (e.g. the
 * synthetic test charts) → the modifier is simply skipped. A weak lord means the sign-only
 * resemblance is EXPECTED to be loose, which is exactly when appearance must not veto the lagna.
 */
function _lagna_lord_condition(natal_chart: any, lord: string): number | null {
  const totals = _GRAHAS
    .map((g) => Number(natal_chart?.shadbala?.[g]?.total ?? NaN))
    .filter((v) => !Number.isNaN(v));
  const lordTotal = Number(natal_chart?.shadbala?.[lord]?.total ?? NaN);
  if (Number.isNaN(lordTotal) || totals.length === 0) return null;
  const max = Math.max(...totals);
  const min = Math.min(...totals);
  if (max <= min) return 0.5;
  return Math.max(0, Math.min(1, (lordTotal - min) / (max - min)));
}

export interface TraitOption {
  label: string;
  /** sign index -> support (1.0 primary, ~0.3-0.8 secondary). An EMPTY map means the option
   *  carries no classical sign signature (an honest "no source" / neutral choice). */
  signs: Record<number, number>;
}

/**
 * Which sign of the chart a trait is scored against — the astrological basis for using it in
 * birth-time rectification:
 *   • "lagna" — physical appearance, read from the ASCENDANT (Vol_06 §2.1–2.3/§2.5). The lagna
 *     cycles through all 12 signs across a day, so a physical match DIRECTLY discriminates the
 *     rising sign → the birth time. This is the classical rectification signal (Vol_06 Part 11).
 *   • "moon" — disposition/temperament & speech, read from the MOON SIGN (Vol_06 §3.1, Vol_11
 *     §7.1). The Moon stays in one sign ~2¼ days, so these barely vary within a day — they are a
 *     SECONDARY, low-weight signal that only narrows the time on the ~40% of days the Moon changes
 *     sign during the search window. They are NEVER scored against the rising sign (that would be
 *     the classic Moon-vs-Lagna confusion the engine warns about).
 */
export type TraitTarget = "lagna" | "moon";

export interface TraitQuestion {
  key: string;
  prompt: string;
  weight: number;
  /** The sign this question scores (see TraitTarget). */
  target: TraitTarget;
  /** The source section(s) every option's sign map traces to (no un-sourced assumptions). */
  source: string;
  /** One line on why it bears on rectification. */
  relevance: string;
  options: Record<string, TraitOption>;
}

// Each question carries: key, prompt, weight, TARGET sign (lagna vs moon — the astrological basis
// for rectification, see TraitTarget), SOURCE section(s) every option traces to, a one-line
// RELEVANCE note, and options mapping an answer to per-sign support (1.0 primary, ~0.3-0.8
// secondary; an EMPTY map = no classical signature, a neutral choice). Multi-select: the engine
// sums the chosen options' support for the target sign (capped per question).
//
// SOURCING: the PHYSICAL (target "lagna") dims are anchored to the Vedic source_learning — Vol_06
// §2.2 (per-sign body/face/eyes/nose/gait), §2.3 (planet-in-Ascendant), §2.5 (complexion). The
// PERSONALITY (target "moon") dims deliberately DEPART from source_learning (owner decision
// 2026-06-20): they use MODERN WESTERN astrology (element × modality archetypes) and are treated as
// WEAK, Barnum-prone evidence — see the personality section comment below. Options with no signature
// get an empty map (they still let a user answer honestly, but assert nothing about a sign).

/** Personality questions (MODERN astrology) — scored against the MOON sign, kept light/weak;
 *  excluded from the photo schema and from the physical (rising-sign) appearance SUMMARY. */
export const PERSONALITY_QUESTION_KEYS = new Set(["psych_style", "emotional", "social_behaviour", "relationship"]);

// ── Birth-circumstance question keys (2026-07-20) ────────────────────────────────────────────
// Birth FACTS scored against the CANDIDATE CHART, not against a static per-sign table:
//   • birth_place_kind   — 06_birth_circumstances "Place of Delivery — Dual Sign Ascendant or
//                          Navamsa" / "— Fixed Sign" / "— Vargottama Ascendant"
//   • delivery_ease      — 06_birth_circumstances "Ease of Delivery — Benefic Aspects" /
//                          "Difficult Delivery — Malefic Aspects"
//   • father_absent_birth— 06_birth_circumstances "Father Absent at Birth"
//   • birth_remedy_lore  — 12_gandanta_births (a gandanta birth MANDATES specific remedies —
//                          puja/charity/father's viewing restriction — which is memorable family lore)
// Their option→sign maps are EMPTY: the check depends on the candidate chart (dual NAVAMSA lagna
// flips every ~13 min; drishti on the 1st changes with the rising sign; the gandanta windows are
// fine 12–48 min bands), so the chart-free sign surfaces (`_target_scores` and the per-question
// match) treat them as NEUTRAL — they discriminate WITHIN a sign, which chart-level scoring in
// score_traits_for_candidate handles via bounded multipliers (never a veto; unknown = no-op).
export const BIRTH_PLACE_KEY = "birth_place_kind";
/** EXACT key contract: special_points.ts reads this trait key (do not rename). */
export const DELIVERY_EASE_KEY = "delivery_ease";
export const FATHER_ABSENT_KEY = "father_absent_birth";
export const REMEDY_LORE_KEY = "birth_remedy_lore";
/** The chart-scored birth-circumstance questions — excluded from every ADDITIVE per-sign path. */
export const CHART_CIRCUMSTANCE_KEYS = new Set([
  BIRTH_PLACE_KEY,
  DELIVERY_EASE_KEY,
  FATHER_ABSENT_KEY,
  REMEDY_LORE_KEY,
]);

/** Questions a PHOTO cannot read: every Moon-targeted disposition dimension (= the personality
 *  dims), plus `delivery_type` and the birth-circumstance questions — birth FACTS (family lore /
 *  hospital records) that no photo can show, even though they are lagna-targeted. */
export const NON_VISUAL_QUESTION_KEYS = new Set([
  ...PERSONALITY_QUESTION_KEYS,
  "delivery_type",
  ...CHART_CIRCUMSTANCE_KEYS,
]);

export const TRAIT_QUESTIONS: TraitQuestion[] = [
  // ════ PHYSICAL — scored against the ASCENDANT (lagna). Vol_06 §2.1: appearance = rising sign +
  //      lagna lord + planets in/aspecting it + Navamsa lagna lord + strongest planet. The lagna
  //      cycles all 12 signs in a day → these are the classical rectification discriminator
  //      (Vol_06 Part 11 rectifies on body type → complexion → eyes/features, never personality). ══
  {
    key: "build",
    target: "lagna",
    source:
      "Vol_06 §2.2 (per-sign build) + §2.3 (Sun square, Moon round, Mars/Saturn lean, Jupiter corpulent) + 06_chart_rectification Step 1 (body-type table)",
    relevance: "Body frame is the first rising-sign read in the Vol_06 Part 11 rectification protocol.",
    // Read RELATIVE to the person's own family/population & age — the classical scale ranks within
    // a population, not against one global standard.
    prompt: "Overall body build — compared with their own family / people of the same age?",
    weight: 1.0,
    options: {
      // Step 1: "Lean and emaciated → Saturn-influenced, Aquarius, Capricorn" — Aquarius gets a
      // SECONDARY 0.4 only, because §2.2 reads Aquarius as "medium build" (kept at 0.7 below);
      // Step 1 is the sole lean/wiry statement for it, so it must not rival the primaries.
      lean:   { label: "Lean / slim / wiry (for their family & age)",    signs: { [GEMINI]: 1.0, [SCORPIO]: 1.0, [VIRGO]: 0.7, [AQUARIUS]: 0.4 } },
      medium: { label: "Medium / average",                               signs: { [LIBRA]: 1.0, [ARIES]: 0.7, [AQUARIUS]: 0.7, [PISCES]: 0.7, [VIRGO]: 0.6 } },
      // Step 1: "Stocky, solid → Taurus, Cancer, Capricorn" — Capricorn secondary 0.5; its §2.2
      // primary read stays `large` ("Large build ... large overall frame").
      stocky: { label: "Stocky / solid / sturdy",                        signs: { [TAURUS]: 1.0, [CANCER]: 0.8, [CAPRICORN]: 0.5 } },
      // Step 1: "Athletic and muscular → Aries, Sagittarius, Mars-influenced";
      // 06_ascendant_physical_appearance: Sagittarius "Well-built, often athletic".
      athletic: { label: "Athletic / muscular",                          signs: { [ARIES]: 1.0, [SAGITTARIUS]: 1.0 } },
      // Sagittarius removed from `large` (2026-07-06): Step 1 files it under athletic/muscular, NOT
      // "Large and commanding → Leo, Jupiter-influenced".
      large:  { label: "Large / heavy / broad (for their family & age)", signs: { [LEO]: 1.0, [CAPRICORN]: 1.0, [CANCER]: 0.8, [ARIES]: 0.6 } },
    },
  },
  {
    key: "height",
    target: "lagna",
    source:
      "Vol_06 §2.2 (Gemini tall/medium, Virgo medium, Sagittarius long-backed) + §2.3 (Saturn 'lean and tall') + 06_chart_rectification Step 1 ('Tall or wiry → Gemini, Virgo, Aquarius')",
    relevance: "Stature narrows the rising sign; population-relative, so it shortlists rather than decides.",
    // Height only means anything as a percentile within the person's own population.
    prompt: "Height — compared with their own family / population?",
    weight: 0.5,
    options: {
      // The source gives NO sign a "short" stature signature (Taurus/Cancer are BUILD reads, Pisces
      // is "medium BUILD") — neutral, asserts nothing (2026-07-02 source-perfection pass; the old
      // Taurus/Cancer/Pisces hints were un-sourced).
      short:  { label: "Shorter than most around them", signs: {} },
      // Sourced only: Gemini "tall or medium", Virgo "medium height". The old un-sourced
      // Aries/Leo/Libra/Aquarius/Pisces defaults removed (Leo is "large, commanding" — no stature).
      medium: { label: "About average for their group", signs: { [GEMINI]: 0.5, [VIRGO]: 0.5 } },
      // 06_chart_rectification Step 1: "Tall or wiry → Gemini, Virgo, Aquarius" — Aquarius gets a
      // SECONDARY 0.4 (its §2.2 read is "medium build" with no stature line, so Step 1 is the only
      // height statement and must stay weak). 06: Capricorn is "large build", no stature claim → removed.
      tall:   { label: "Taller than most around them",  signs: { [GEMINI]: 1.0, [SAGITTARIUS]: 0.8, [AQUARIUS]: 0.4 } },
    },
  },
  {
    key: "complexion",
    target: "lagna",
    source: "Vol_06 §2.5 (complexion-by-sign table) + §2.2",
    relevance: "Classical per-sign complexion (§2.5); a near-independent rising-sign read alongside build.",
    // The §2.5 scale is RELATIVE (fairest→darkest within a population); the warm/reddish undertone
    // is a separate axis that can sit on any shade.
    prompt: "Skin tone — relative to their own family/community (fair, medium or dark for their group; skip if unsure):",
    weight: 0.7,
    options: {
      // §2.5: Taurus white, Leo white/gold, Cancer pale-red/whitish, Pisces pale/whitish.
      fair:     { label: "Fairer / lighter than most around them", signs: { [TAURUS]: 1.0, [LEO]: 0.9, [CANCER]: 0.8, [PISCES]: 0.8 } },
      // complexion table: Virgo variegated, Gemini grass-green/dusky, Sagittarius tawny/golden;
      // Capricorn "variegated OR dark" → its variegated half maps here (like Virgo), dark half below.
      wheatish: { label: "Wheatish / medium",                      signs: { [VIRGO]: 0.8, [GEMINI]: 0.7, [SAGITTARIUS]: 0.6, [CAPRICORN]: 0.5 } },
      // complexion table: Libra black, Capricorn variegated/dark, Aquarius deep brown.
      dark:     { label: "Darker / deeper than most around them",  signs: { [LIBRA]: 1.0, [CAPRICORN]: 0.9, [AQUARIUS]: 0.8 } },
      // complexion table: Aries blood-red, Scorpio reddish-brown, Cancer pale-red/pinkish.
      // Sagittarius "tawny/golden" is captured by `wheatish`, not reddish → removed here.
      reddish:  { label: "Warm / reddish / coppery undertone (any shade)", signs: { [ARIES]: 1.0, [SCORPIO]: 0.9, [CANCER]: 0.6 } },
    },
  },
  {
    key: "face",
    target: "lagna",
    source: "Vol_06 §2.2 (per-sign face shape)",
    relevance: "Face shape is a sharp rising-sign discriminator in the §2.2 signatures.",
    prompt: "Face shape?",
    weight: 0.8,
    options: {
      // §2.2: Cancer/Taurus round-full, Pisces rounded, Aquarius full-round face.
      round:     { label: "Round / full",                 signs: { [CANCER]: 1.0, [TAURUS]: 1.0, [PISCES]: 0.8, [AQUARIUS]: 0.7 } },
      // §2.2: Aries is the ONLY sign given a sharp FACE ("lean face, sharp features") — the old
      // Scorpio/Gemini cells were inferences from their BUILD/eye reads, not stated face shapes.
      sharp:     { label: "Sharp / angular / lean",       signs: { [ARIES]: 1.0 } },
      // §2.2: Leo "wide face" — the only sign given a broad-face signature.
      broad:     { label: "Broad / wide",                 signs: { [LEO]: 1.0 } },
      // §2.2: Capricorn "Coarse features" — its distinctive face signature.
      coarse:    { label: "Coarse / rough-hewn features", signs: { [CAPRICORN]: 0.8 } },
      // §2.2: Libra symmetrical, Virgo beautiful/clear, Sagittarius even. Pisces is WEAK (0.3):
      // the source gives it "Rounded features" and "Beautiful EYES", not a beautiful face —
      // `round` 0.8 carries its primary face read.
      beautiful: { label: "Symmetrical / fine / elegant", signs: { [LIBRA]: 1.0, [VIRGO]: 0.7, [PISCES]: 0.3, [SAGITTARIUS]: 0.5 } },
    },
  },
  {
    key: "forehead",
    target: "lagna",
    source: "Vol_06 §2.2 (Aries 'prominent head or forehead'; Leo 'wide face and forehead') + §2.3 (Sun 'prominent forehead')",
    relevance: "A distinct §2.2 head/forehead marker for Aries vs Leo; refines the face read.",
    prompt: "Forehead?",
    weight: 0.4,
    options: {
      broad_high: { label: "Broad / high / wide",     signs: { [LEO]: 1.0 } },
      prominent:  { label: "Prominent / forward",     signs: { [ARIES]: 1.0 } },
      ordinary:   { label: "Ordinary / unremarkable", signs: {} },
    },
  },
  {
    key: "eyes",
    target: "lagna",
    source: "Vol_06 §2.2 (Cancer large, Taurus bull-like, Libra wide/round; Scorpio piercing, Gemini sharp) + §2.3 (Moon/Jupiter large, Mars sharp)",
    relevance: "Eyes are explicitly described per rising sign in §2.2 — a strong discriminator.",
    prompt: "Eyes — size & expression?",
    weight: 0.8,
    options: {
      // §2.2: Cancer "large eyes", Taurus "prominent eyes, bull-like", Libra "wide, round eyes".
      large_round:    { label: "Large / round / prominent eyes", signs: { [CANCER]: 1.0, [TAURUS]: 0.9, [LIBRA]: 0.8 } },
      // §2.2: Scorpio "piercing, sharp eyes", Gemini "sharp, expressive eyes"; Aries sharp features + §2.3 Mars "sharp eyes".
      sharp_piercing: { label: "Sharp / piercing eyes",          signs: { [SCORPIO]: 1.0, [GEMINI]: 0.8, [ARIES]: 0.6 } },
      // No SIGN carries a small/deep-set signature — but Saturn-in-Lagna does ("red, DEPRESSED
      // (sunken) eyes", 06_chart_rectification Step 3), credited via PLANET_LAGNA_TRAITS.
      small_deep:     { label: "Small / deep-set eyes",          signs: {} },
    },
  },
  {
    key: "nose",
    target: "lagna",
    source: "Vol_06 §2.2 (Libra 'long nose', Gemini & Scorpio 'prominent nose')",
    relevance: "Nose is named for exactly three signs in §2.2 — a clean three-sign discriminator.",
    prompt: "Nose?",
    weight: 0.7,
    options: {
      prominent_long: { label: "Prominent / long nose",    signs: { [LIBRA]: 1.0, [GEMINI]: 0.9, [SCORPIO]: 0.8 } },
      // §10.6 calls a small/even nose generally auspicious but ties it to no sign — neutral here.
      small_neat:     { label: "Small / neat / even nose",  signs: {} },
      ordinary:       { label: "Ordinary / unremarkable",   signs: {} },
    },
  },
  {
    key: "hairline",
    target: "lagna",
    source: "Vol_06 §2.2 (Leo 'sparse hair, especially later in life') + §2.3 (Sun 'limited hair')",
    relevance: "Early thinning is the one hair signature §2.2 fixes to a sign (Leo). AGE-GATED ≥25.",
    // Gender-neutral wording. AGE-GATED ≥25 (see age_blocked_options).
    prompt: "Hairline — thinning / receding early for their age?",
    weight: 0.4,
    options: {
      receding: { label: "Thinning / receding (early for their age)", signs: { [LEO]: 1.0 } },
      full:     { label: "Full / no early thinning",                  signs: {} },
    },
  },
  {
    key: "teeth",
    target: "lagna",
    source: "Vol_06 §2.2 (Capricorn 'prominent teeth') + §2.3 (Saturn 'large teeth')",
    relevance: "Prominent teeth is a §2.2 Capricorn signature — a narrow but specific marker.",
    prompt: "Teeth?",
    weight: 0.4,
    options: {
      prominent: { label: "Prominent / large teeth", signs: { [CAPRICORN]: 1.0 } },
      // §10.6 even/milk-white teeth = auspicious but not sign-specific — neutral.
      even:      { label: "Even / regular teeth",     signs: {} },
    },
  },
  {
    key: "body_hair",
    target: "lagna",
    source: "Vol_06 §2.2 (Scorpio 'hairy body')",
    relevance: "A hairy body is §2.2's distinctive Scorpio-rising marker.",
    // Gender-neutral: "more body hair than typical in their family".
    prompt: "Body hair — more than typical in their family?",
    weight: 0.3,
    options: {
      heavy: { label: "Noticeably more body hair",   signs: { [SCORPIO]: 1.0 } },
      light: { label: "Average / little body hair",  signs: {} },
    },
  },
  {
    key: "neck_shoulders",
    target: "lagna",
    source: "Vol_06 §2.2 (Taurus 'short neck, strong shoulders'; Leo 'broad chest'; Cancer 'wide chest') + §2.3 (Jupiter 'big chest')",
    relevance: "Neck/chest breadth separates Taurus (short, strong) from Leo/Cancer (broad).",
    prompt: "Neck & shoulders / upper body?",
    weight: 0.5,
    options: {
      short_thick: { label: "Short neck / strong shoulders", signs: { [TAURUS]: 1.0 } },
      broad:       { label: "Broad chest / shoulders",       signs: { [LEO]: 0.9, [CANCER]: 0.8 } },
      // No sign carries an explicit 'slender neck' signature — neutral.
      slender:     { label: "Slender / narrow upper body",   signs: {} },
    },
  },
  {
    key: "limbs",
    target: "lagna",
    source: "Vol_06 §2.2 (Gemini 'long arms'; Virgo 'long, elegant hands'; Pisces 'small feet')",
    relevance: "Limb proportion is named for Gemini/Virgo (long) and Pisces (small feet) in §2.2.",
    prompt: "Hands / arms / limbs?",
    weight: 0.4,
    options: {
      long_elegant: { label: "Long / elegant limbs / hands", signs: { [GEMINI]: 1.0, [VIRGO]: 0.8 } },
      short:        { label: "Short / small hands or feet",  signs: { [PISCES]: 0.5 } },
      average:      { label: "Average / unremarkable",       signs: {} },
    },
  },
  {
    key: "bearing",
    target: "lagna",
    source: "Vol_06 §2.2 (per-sign gait/bearing) + §2.3 (Mars energetic, Venus graceful)",
    relevance: "Gait is described per sign in §2.2 and is directly observable — a good rising-sign read.",
    // One option = ONE distinct way of moving; synonyms joined with "/".
    prompt: "How do they carry themselves / move? (pick each that fits)",
    weight: 0.8,
    options: {
      quick:         { label: "Quick / fast-moving",            signs: { [ARIES]: 1.0, [GEMINI]: 0.8 } },
      energetic:     { label: "Energetic / lively",             signs: { [ARIES]: 1.0, [GEMINI]: 0.6 } },
      slow:          { label: "Slow / unhurried",               signs: { [CANCER]: 0.9, [TAURUS]: 0.5 } },
      steady:        { label: "Steady / even-paced",            signs: { [TAURUS]: 1.0, [CANCER]: 0.5 } },
      deliberate:    { label: "Deliberate / measured",          signs: { [TAURUS]: 1.0 } },
      graceful:      { label: "Graceful / smooth",              signs: { [LIBRA]: 1.0, [PISCES]: 0.7 } },
      fluid:         { label: "Soft / fluid / flowing",         signs: { [PISCES]: 1.0, [LIBRA]: 0.5 } },
      regal:         { label: "Regal / majestic",               signs: { [LEO]: 1.0 } },
      authoritative: { label: "Authoritative / commanding",     signs: { [LEO]: 1.0 } },
      // §2.2: "Noble bearing" is Sagittarius's VERBATIM primary marker → full 1.0.
      noble:         { label: "Noble / dignified",              signs: { [SAGITTARIUS]: 1.0, [LEO]: 0.5 } },
      // 06_ascendant_physical_appearance: Scorpio/Aquarius purposeful; Libra "graceful, PURPOSEFUL walk".
      purposeful:    { label: "Purposeful",                     signs: { [SCORPIO]: 0.9, [AQUARIUS]: 0.9, [LIBRA]: 0.7 } },
      direct:        { label: "Direct / straightforward",       signs: { [SCORPIO]: 0.8, [ARIES]: 0.5 } },
      precise:       { label: "Careful / precise / methodical", signs: { [VIRGO]: 1.0 } },
    },
  },
  {
    // ── Rectification Step 6 — DELIVERY TYPE CONFIRMATION (06_chart_rectification) ──
    // The signs are classified by RISE DIRECTION (01_signs_classification "Rising Direction"):
    // Sirshodaya (head-rising), Prishtodaya (back-rising), Ubhayodaya (both-rising, Pisces only).
    // How the baby PRESENTED at delivery mirrors how the rising sign rises
    // (06_birth_circumstances "Type of Delivery" — "Determined at the moment of birth by the
    // Ascendant sign"). Each option's sign set is quoted verbatim from the source; every support
    // is a full 1.0 because the source frames this as a BINARY confirmation, and the three
    // options PARTITION all 12 signs. The elimination half of the gate (a known delivery type
    // RULES OUT the non-matching classes) is auto-embedded by combined_trait_answers below.
    // NO age gating: this is a birth fact, equally meaningful at any age.
    key: "delivery_type",
    target: "lagna",
    source:
      "06_chart_rectification Step 6 (Delivery Type Confirmation) + 06_birth_circumstances 'Type of Delivery' + 01_signs_classification 'Rising Direction' (Sirshodaya/Prishtodaya/Ubhayodaya)",
    relevance:
      "Delivery presentation mirrors the rising sign's rise direction — a binary confirmation that eliminates the non-matching half of the candidate signs (Step 6).",
    prompt:
      "How was the person born — which part came first? (Often known family lore — ask a parent. " +
      "Skip if unknown, or if it was a planned C-section before labour began.)",
    weight: 0.8,
    options: {
      // Head-first (normal) → Sirshodaya (head-rising): Gemini, Leo, Virgo, Libra, Scorpio, Aquarius.
      head_first:   { label: "Head-first (normal delivery)",                 signs: { [GEMINI]: 1.0, [LEO]: 1.0, [VIRGO]: 1.0, [LIBRA]: 1.0, [SCORPIO]: 1.0, [AQUARIUS]: 1.0 } },
      // Feet-first (breech) → Prishtodaya (back-rising): Aries, Taurus, Cancer, Sagittarius, Capricorn.
      feet_first:   { label: "Feet-first / breech",                          signs: { [ARIES]: 1.0, [TAURUS]: 1.0, [CANCER]: 1.0, [SAGITTARIUS]: 1.0, [CAPRICORN]: 1.0 } },
      // Unusual arm/hand presentation → Pisces, the sole Ubhayodaya (both-rising) sign.
      unusual_limb: { label: "Hand or arm came first / unusual position",    signs: { [PISCES]: 1.0 } },
    },
  },
  // ════ BIRTH CIRCUMSTANCES — birth facts scored against the CANDIDATE CHART (see the
  //      CHART_CIRCUMSTANCE_KEYS block comment). All sign maps are EMPTY on purpose: the classical
  //      condition needs the chart (dual/fixed NAVAMSA lagna, drishti on the 1st, vargottama,
  //      gandanta windows), so these contribute NOTHING to the chart-free sign ranking and instead
  //      apply bounded multipliers per candidate in score_traits_for_candidate. Like delivery_type
  //      they are NEVER age-gated (a birth fact means the same at any age) and never photo-read. ══
  {
    key: BIRTH_PLACE_KEY,
    target: "lagna",
    source:
      "06_birth_circumstances 'Place of Delivery — Dual Sign Ascendant or Navamsa' (dual D1 or dual D9 lagna → born while travelling; Vargottama overrides → at home) + '— Fixed Sign' (fixed D1/D9 lagna → one's own established place) + '— Vargottama Ascendant' (same D1/D9 sign → one's own house specifically)",
    relevance:
      "The Navamsa lagna flips every ~13 min, so 'born at home vs on the way' discriminates WITHIN a rising sign — a real intra-sign rectification signal.",
    prompt:
      "Where was the person born? (Family lore — 'at home', 'at the grandparents' place', 'on the way to the hospital'. Skip or pick unknown if unsure.)",
    weight: 0.5, // nominal — inert in the additive paths (chart-scored, see CHART_CIRCUMSTANCE_KEYS)
    options: {
      own_home:      { label: "In their own family home",                       signs: {} },
      parents_home:  { label: "At the parents' / maternal family home",         signs: {} },
      travelling:    { label: "While travelling / in transit / on the way",     signs: {} },
      hospital:      { label: "In a hospital or elsewhere",                     signs: {} },
      unknown:       { label: "Don't know",                                     signs: {} },
    },
  },
  {
    // EXACT key name `delivery_ease` (option keys contain "easy"/"difficult") — read by
    // special_points.ts; do not rename.
    key: DELIVERY_EASE_KEY,
    target: "lagna",
    source:
      "06_birth_circumstances 'Ease of Delivery — Benefic Aspects' (benefics aspecting the Ascendant → comfortable, easy delivery; strong malefics also aspecting → protection is partial) + 'Difficult Delivery — Malefic Aspects' (malefics aspect the Ascendant without benefic protection → difficult, painful delivery)",
    relevance:
      "Drishti on the 1st house changes with the rising sign, so an easy/difficult delivery cross-checks the candidate Ascendant against the family's account of the birth.",
    prompt:
      "Was the delivery easy or difficult? (As the family tells it. Skip or pick unknown if unsure.)",
    weight: 0.5, // nominal — inert in the additive paths (chart-scored, see CHART_CIRCUMSTANCE_KEYS)
    options: {
      easy:      { label: "Easy / normal delivery",              signs: {} },
      difficult: { label: "Difficult / complicated delivery",    signs: {} },
      unknown:   { label: "Don't know",                          signs: {} },
    },
  },
  {
    key: FATHER_ABSENT_KEY,
    target: "lagna",
    source:
      "06_birth_circumstances 'Father Absent at Birth' (Ascendant unaspected by the Moon → father absent at birth)",
    relevance:
      "Whether the Moon aspects the candidate Ascendant flips with the rising sign — the father's presence/absence at the birth is checkable family lore.",
    prompt:
      "Was the father present at the birth? (Often known family lore. Skip or pick unknown if unsure.)",
    weight: 0.4, // nominal — inert in the additive paths (chart-scored, see CHART_CIRCUMSTANCE_KEYS)
    options: {
      father_present: { label: "Father was present / nearby",            signs: {} },
      father_absent:  { label: "Father was absent / away at the birth",  signs: {} },
      unknown:        { label: "Don't know",                             signs: {} },
    },
  },
  {
    key: REMEDY_LORE_KEY,
    target: "lagna",
    source:
      "12_gandanta_births — every gandanta birth (Tithi/Nakshatra/Lagna) MANDATES remedial rites (cow/gold charity, Havan + Abhisheka) and the father must not see the child until after the rites; such rites are memorable family lore",
    relevance:
      "The gandanta windows are fine 12–48 min bands (lagna ≈3° of rising; Moon ≈2 ghatikas of a nakshatra junction) — remembered post-birth remedies point INTO those bands, a sharp intra-day signal.",
    prompt:
      "Was any special ritual performed soon after the birth, or did the father avoid seeing the child at first? (Memorable family lore in traditional families. Skip if unsure.)",
    weight: 0.4, // nominal — inert in the additive paths (chart-scored, see CHART_CIRCUMSTANCE_KEYS)
    options: {
      remedy_performed: { label: "A special puja / ritual / charity was performed soon after the birth", signs: {} },
      father_avoided:   { label: "The father avoided seeing the child at first",                        signs: {} },
      // "No such thing" is only WEAK evidence of a non-gandanta birth (families forget, or never
      // held the custom) — scored as a very mild non-gandanta credit, near no-op.
      none_unknown:     { label: "No such thing / don't know",                                          signs: {} },
    },
  },
  // ════ PERSONALITY (MODERN ASTROLOGY) — psychological style, emotional tendencies, social
  //      behaviour, relationship patterns. Per owner decision (2026-06-20) this dimension uses
  //      MODERN WESTERN ASTROLOGY (element × modality archetypes — Fire/Earth/Air/Water,
  //      Cardinal/Fixed/Mutable) rather than the Vedic source_learning, and is deliberately
  //      treated as WEAK EVIDENCE: personality descriptions are highly prone to the BARNUM/FORER
  //      effect (almost any chart can be made to "fit"), so the per-sign maps lean on the few
  //      genuinely DISCRIMINATING traits (down-weighting universal "loyal/sensitive/intelligent"
  //      filler) and the whole bucket stays SECONDARY. It is still scored against the MOON SIGN
  //      (the rising-sign-INVARIANT temperament point already plumbed): it stays ~flat within a
  //      day and NEVER decides the rising sign on its own — it only narrows the time when the Moon
  //      changes sign during the search window. Weights are equal (≈0.8 total, _target_scores
  //      renormalises so absolute weight only affects scale, not rank). ════
  {
    key: "psych_style",
    target: "moon",
    source: "Modern Western astrology — element × modality cognitive archetypes (Sun/Mercury-sign psychological style)",
    relevance: "WEAK, Barnum-prone personality signal; Moon-targeted so it never decides the rising sign.",
    prompt: "Psychological style — how they think and approach the world?",
    weight: 0.2,
    options: {
      // Fire/Cardinal Aries acts first; Leo & Sag share the leap-before-looking instinct.
      impulsive_instinctive: { label: "Impulsive / acts on instinct first",     signs: { [ARIES]: 1.0, [LEO]: 0.6, [SAGITTARIUS]: 0.6 } },
      // Earth/Mutable Virgo is the archetypal analyst; Cap & Taurus share the methodical bent.
      analytical_precise:    { label: "Analytical / methodical / detail-focused", signs: { [VIRGO]: 1.0, [CAPRICORN]: 0.6, [TAURUS]: 0.5 } },
      // Water/Fixed Scorpio's penetrating long-game; Cap's strategic discipline.
      strategic_intense:     { label: "Strategic / probing / plays the long game", signs: { [SCORPIO]: 1.0, [CAPRICORN]: 0.7 } },
      // Air Aquarius the contrarian visionary; Gemini the idea-juggler.
      inventive_unconventional: { label: "Inventive / unconventional / abstract", signs: { [AQUARIUS]: 1.0, [GEMINI]: 0.6 } },
      // Fire/Mutable Sagittarius the big-picture philosopher; Libra & Pisces share the idealism.
      philosophical_idealistic: { label: "Philosophical / big-picture / idealistic", signs: { [SAGITTARIUS]: 1.0, [LIBRA]: 0.6, [PISCES]: 0.5 } },
    },
  },
  {
    key: "emotional",
    target: "moon",
    source: "Modern Western astrology — element-based emotional archetypes (express↔suppress, fast↔long-lasting)",
    relevance: "WEAK, Barnum-prone personality signal; Moon-targeted so it never decides the rising sign.",
    prompt: "Emotional tendencies — how they feel and express emotion?",
    weight: 0.2,
    options: {
      // Water signs feel deeply / absorb others; Cancer & Pisces the classic empaths, Scorpio secondary.
      sensitive_empathic: { label: "Deeply sensitive / absorbs others' feelings", signs: { [CANCER]: 1.0, [PISCES]: 1.0, [SCORPIO]: 0.5 } },
      // Scorpio's all-or-nothing, guarded intensity; Aries' hot streak.
      intense_guarded:    { label: "Intense / all-or-nothing / guarded",          signs: { [SCORPIO]: 1.0, [ARIES]: 0.5 } },
      // Fire flares fast then cools — Aries the quick temper, Leo the big expressive feeling.
      volatile_expressive: { label: "Quick-tempered / expressive / flares then cools", signs: { [ARIES]: 1.0, [LEO]: 0.8, [SAGITTARIUS]: 0.5 } },
      // Earth contains and steadies — Taurus even-keeled, Cap stoic, Virgo reserved.
      steady_contained:   { label: "Even-keeled / steady / keeps feelings contained", signs: { [TAURUS]: 1.0, [CAPRICORN]: 0.9, [VIRGO]: 0.7 } },
      // Air processes feeling through logic — Aquarius the most detached, Gemini & Libra secondary.
      detached_cool:      { label: "Detached / processes feelings through logic",  signs: { [AQUARIUS]: 1.0, [GEMINI]: 0.8, [LIBRA]: 0.6 } },
    },
  },
  {
    key: "social_behaviour",
    target: "moon",
    source: "Modern Western astrology — extroversion (Fire/Air) ↔ reserve (Earth/Water), with Aquarius the introvert outlier",
    relevance: "WEAK, Barnum-prone personality signal; Moon-targeted so it never decides the rising sign.",
    prompt: "Social behaviour — how they show up around people?",
    weight: 0.2,
    options: {
      // Leo near-uniquely seeks the spotlight; Aries the dominant initiator.
      attention_magnetic: { label: "Magnetic / loves the spotlight / center stage", signs: { [LEO]: 1.0, [ARIES]: 0.5 } },
      // Air/Fire chatty extroverts — Gemini the social butterfly, Sag & Libra secondary.
      gregarious_witty:   { label: "Gregarious / talkative / social butterfly",     signs: { [GEMINI]: 1.0, [SAGITTARIUS]: 0.7, [LIBRA]: 0.6 } },
      // Libra the peacemaker; Pisces & Cancer the gentle accommodators.
      diplomatic_charming: { label: "Diplomatic / charming / keeps the peace",       signs: { [LIBRA]: 1.0, [PISCES]: 0.5, [CANCER]: 0.5 } },
      // Fire bluntness — Aries & Sag say it straight, Scorpio cuttingly.
      blunt_direct:       { label: "Blunt / direct / says it straight",             signs: { [ARIES]: 1.0, [SAGITTARIUS]: 0.8, [SCORPIO]: 0.5 } },
      // Earth/Water reserve, small circle — Virgo/Cap/Scorpio/Cancer/Taurus.
      reserved_private:   { label: "Reserved / private / small circle",             signs: { [VIRGO]: 0.8, [CAPRICORN]: 0.8, [SCORPIO]: 0.7, [CANCER]: 0.6, [TAURUS]: 0.6 } },
      // Aquarius near-uniquely the detached, group-minded independent.
      independent_detached: { label: "Independent / detached / group-minded",        signs: { [AQUARIUS]: 1.0, [CAPRICORN]: 0.5 } },
    },
  },
  {
    key: "relationship",
    target: "moon",
    source: "Modern Western astrology — attachment style: closeness/possessive (Fixed/Water) ↔ freedom/detached (Mutable/Air); the most DISCRIMINATING dimension",
    relevance: "WEAK personality signal (least Barnum-prone of the four); Moon-targeted so it never decides the rising sign.",
    prompt: "Relationship patterns — how they relate to partners and intimacy?",
    weight: 0.2,
    options: {
      // Committed-attachment archetypes — Taurus (fixed earth) the steadfast primary; Scorpio
      // fixed water, Cancer/Capricorn cardinal but equally loyal once in.
      devoted_loyal:        { label: "Devoted / loyal / steady once committed",      signs: { [TAURUS]: 1.0, [CANCER]: 0.8, [SCORPIO]: 0.7, [CAPRICORN]: 0.6 } },
      // Scorpio's possessive, all-or-nothing passion; Taurus possessive, Aries ardent.
      possessive_passionate: { label: "Passionate / possessive / all-or-nothing",     signs: { [SCORPIO]: 1.0, [TAURUS]: 0.7, [ARIES]: 0.5 } },
      // Mutable/Air freedom-seekers — Sag & Aquarius commitment-shy, Gemini needs novelty.
      freedom_loving:       { label: "Freedom-loving / commitment-shy / needs space", signs: { [SAGITTARIUS]: 1.0, [AQUARIUS]: 0.9, [GEMINI]: 0.8, [ARIES]: 0.5 } },
      // Pisces & Libra the romantic idealists; Leo the grand romantic.
      idealistic_romantic:  { label: "Idealistic / romantic / dreamy",               signs: { [PISCES]: 1.0, [LIBRA]: 0.9, [LEO]: 0.5 } },
      // Earth caution — Cap & Virgo slow to trust, Scorpio guarded.
      cautious_guarded:     { label: "Cautious / slow to trust / guarded",           signs: { [CAPRICORN]: 1.0, [VIRGO]: 0.9, [SCORPIO]: 0.5 } },
      // Cancer the archetypal caretaker; Taurus/Virgo/Pisces secondary.
      nurturing_protective: { label: "Nurturing / protective / caretaking",          signs: { [CANCER]: 1.0, [TAURUS]: 0.5, [VIRGO]: 0.5, [PISCES]: 0.5 } },
    },
  },
];

/**
 * Legacy → current option remap (2026 split). Old saved profiles answered under the retired
 * `standout`, `marks`, and `temperament` keys; this maps each old option to its new
 * {question, option} so those answers KEEP scoring instead of silently vanishing. Applied in
 * normalize_trait_answers (so it also covers rejected-trait gates). Options with no current home
 * (e.g. `none`, the un-sourced `thick_hair` since the `hair`-texture dimension was dropped) drop.
 */
const _LEGACY_OPTION_REMAP: Record<string, Record<string, [string, string]>> = {
  standout: {
    nose: ["nose", "prominent_long"],
    eyes_big: ["eyes", "large_round"],
    eyes_sharp: ["eyes", "sharp_piercing"],
  },
  marks: {
    balding: ["hairline", "receding"],
    hairy: ["body_hair", "heavy"],
    teeth: ["teeth", "prominent"],
    // `thick_hair` and `none` have no current home → drop.
  },
  // ── Pre-2026-06-20 Vedic personality dims → new modern-astrology dims (psych_style / emotional /
  //    social_behaviour / relationship). Keeps old saved answers scoring instead of vanishing. ──
  pace_energy: {
    restless: ["psych_style", "impulsive_instinctive"], energetic: ["psych_style", "impulsive_instinctive"],
    steady: ["emotional", "steady_contained"],
  },
  social_style: {
    outgoing_charming: ["social_behaviour", "gregarious_witty"], reserved_private: ["social_behaviour", "reserved_private"],
    diplomatic: ["social_behaviour", "diplomatic_charming"], blunt: ["social_behaviour", "blunt_direct"],
  },
  mind_style: {
    analytical_precise: ["psych_style", "analytical_precise"], dreamy_imaginative: ["psych_style", "philosophical_idealistic"],
    philosophical: ["psych_style", "philosophical_idealistic"], strategic: ["psych_style", "strategic_intense"],
  },
  drive: {
    commanding_proud: ["social_behaviour", "attention_magnetic"], ambitious_disciplined: ["psych_style", "strategic_intense"],
    nurturing: ["relationship", "nurturing_protective"], comfort: ["relationship", "devoted_loyal"],
  },
  temperament: {
    bold: ["social_behaviour", "attention_magnetic"], impulsive: ["psych_style", "impulsive_instinctive"], competitive: ["psych_style", "impulsive_instinctive"],
    patient: ["emotional", "steady_contained"], sensual: ["relationship", "devoted_loyal"], stubborn: ["emotional", "steady_contained"],
    talkative: ["social_behaviour", "gregarious_witty"], curious: ["psych_style", "inventive_unconventional"], witty: ["social_behaviour", "gregarious_witty"],
    nurturing: ["relationship", "nurturing_protective"], emotional: ["emotional", "sensitive_empathic"], homely: ["relationship", "nurturing_protective"],
    proud: ["social_behaviour", "attention_magnetic"], dramatic: ["social_behaviour", "attention_magnetic"], commanding: ["social_behaviour", "attention_magnetic"], generous: ["relationship", "nurturing_protective"],
    analytical: ["psych_style", "analytical_precise"], critical: ["psych_style", "analytical_precise"], modest: ["social_behaviour", "reserved_private"],
    diplomatic: ["social_behaviour", "diplomatic_charming"], charming: ["social_behaviour", "diplomatic_charming"], indecisive: ["social_behaviour", "diplomatic_charming"],
    intense: ["emotional", "intense_guarded"], secretive: ["social_behaviour", "reserved_private"], strategic: ["psych_style", "strategic_intense"],
    optimistic: ["psych_style", "philosophical_idealistic"], adventurous: ["relationship", "freedom_loving"], blunt: ["social_behaviour", "blunt_direct"],
    ambitious: ["psych_style", "strategic_intense"], reserved: ["social_behaviour", "reserved_private"], responsible: ["relationship", "cautious_guarded"],
    unconventional: ["psych_style", "inventive_unconventional"], humanitarian: ["social_behaviour", "independent_detached"], detached: ["emotional", "detached_cool"],
    compassionate: ["relationship", "nurturing_protective"], dreamy: ["psych_style", "philosophical_idealistic"], escapist: ["emotional", "sensitive_empathic"],
  },
};

/**
 * Translate any retired question keys in an answers object to their current {question:option}
 * (merging when several old options map to the same new key). Current keys pass through
 * untouched; non-array values (e.g. the embedded `_rejected` object) pass through as-is.
 */
function _apply_legacy_remap(answers: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  const add = (key: string, vals: string[]) => {
    out[key] = (Array.isArray(out[key]) ? out[key] : []).concat(vals);
  };
  for (const [key, raw] of Object.entries(answers)) {
    const remap = _LEGACY_OPTION_REMAP[key];
    if (remap) {
      const vals = typeof raw === "string" ? [raw] : Array.isArray(raw) ? raw : [];
      for (const v of vals) {
        const mapped = remap[v as string];
        if (mapped) add(mapped[0], [mapped[1]]);
      }
    } else if (typeof raw === "string") {
      add(key, [raw]);
    } else if (Array.isArray(raw)) {
      add(key, raw as string[]);
    } else if (!(key in out)) {
      out[key] = raw; // pass through (e.g. the _rejected object)
    }
  }
  return out;
}

// ── Age gating ───────────────────────────────────────────────────────────────────────────
// The Vol_06 signatures describe ADULT bodies. Some markers are developmental noise on a
// child (height/build are growth-stage; "early balding" is meaningless under ~25) and would
// push the rising-sign shortlist around for exactly the users (children) who depend on
// appearance most, because their few events rarely discriminate. These rules drop the
// age-inappropriate OPTIONS (or whole questions) from the questionnaire, the photo schema,
// the chat catalog, and — defensively — from saved answers at scoring time.
export const ADULT_PATTERN_MIN_AGE = 25; // "early balding / thinning for their age" needs adulthood to mean anything
export const GROWTH_STAGE_MAX_AGE = 12; // below this, height/build are growth-stage, not signature

/** Age in years from an ISO birth date, or null if unparseable. */
export function age_years_from_birth(birth_date_iso: string | null | undefined, on: PlainDate | null = null): number | null {
  const born = tryParseISODate(birth_date_iso);
  if (born === null) return null;
  const today = on ?? todayLocal();
  return Math.max(0.0, diffDays(today, born) / 365.2425);
}

/**
 * question_key -> option keys that are developmentally meaningless at this age.
 * A value of {"*"} blocks the whole question. Empty dict when age is unknown or adult.
 */
export function age_blocked_options(age_years: number | null): Record<string, Set<string>> {
  if (age_years === null || age_years === undefined) return {};
  const blocked: Record<string, Set<string>> = {};
  if (age_years < ADULT_PATTERN_MIN_AGE) {
    // Early thinning/receding hairline is meaningless before adulthood (was `marks:balding`).
    blocked["hairline"] = new Set(["receding"]);
  }
  if (age_years < GROWTH_STAGE_MAX_AGE) {
    blocked["height"] = new Set(["*"]);
    blocked["build"] = new Set(["*"]);
  }
  return blocked;
}

/**
 * TRAIT_QUESTIONS with age-inappropriate options/questions removed — what every
 * capture surface (questionnaire, photo schema, chat catalog) should present.
 */
export function trait_questions_for_age(age_years: number | null): TraitQuestion[] {
  const blocked = age_blocked_options(age_years);
  if (Object.keys(blocked).length === 0) return TRAIT_QUESTIONS;
  const questions: TraitQuestion[] = [];
  for (const question of TRAIT_QUESTIONS) {
    const bad = blocked[question.key];
    if (bad === undefined) {
      questions.push(question);
      continue;
    }
    if (bad.has("*")) continue;
    const options: Record<string, TraitOption> = {};
    for (const [k, v] of Object.entries(question.options)) {
      if (!bad.has(k)) options[k] = v;
    }
    if (Object.keys(options).length > 0) {
      questions.push({ ...question, options });
    }
  }
  return questions;
}

// Planet-in-Ascendant modifiers (Vol_06 2.3): a planet sitting in the rising sign pushes
// the appearance toward that planet's nature, overriding the pure sign signature. If the
// user's answer matches the planet's signature, the candidate gets credit even when the
// Ascendant sign alone would not. Maps planet -> question_key -> {supported answers}.
export const PLANET_LAGNA_TRAITS: Record<string, Record<string, Set<string>>> = {
  // Each cell quoted verbatim from the Vol_06 §2.3 table — no inferred modifiers.
  sun:     { complexion: new Set(["reddish"]), build: new Set(["medium"]), forehead: new Set(["prominent"]), hairline: new Set(["receding"]) }, // honey eyes, SQUARE build, bilious/reddish, LIMITED HAIR, PROMINENT FOREHEAD
  moon:    { complexion: new Set(["fair"]), face: new Set(["round"]), eyes: new Set(["large_round"]), build: new Set(["stocky", "medium"]) },   // ROUND body, FAIR, LARGE EYES, FULL FACE
  mars:    { complexion: new Set(["reddish"]), build: new Set(["lean"]), eyes: new Set(["sharp_piercing"]), bearing: new Set(["quick", "energetic"]) }, // LEAN, blood-red, SHARP EYES, ENERGETIC bearing
  mercury: { complexion: new Set(["wheatish"]), face: new Set(["beautiful"]), eyes: new Set(["large_round"]) },                                  // green complexion, ATTRACTIVE features, BROAD/reddish eyes (06_chart_rectification Step 3)
  jupiter: { build: new Set(["large"]), complexion: new Set(["wheatish"]), eyes: new Set(["large_round"]), neck_shoulders: new Set(["broad"]) }, // LARGE/corpulent, "Golden tinge to complexion" / "Like pure yellow metal (golden)" = wheatish (the golden bucket, like Sag), BIG CHEST, PROMINENT EYES
  venus:   { complexion: new Set(["wheatish"]), face: new Set(["beautiful"]), eyes: new Set(["large_round"]) },                                  // BEAUTIFUL features, splendorous, BROAD/attractive eyes (06_chart_rectification Step 3); complexion "Mix of yellow and green; splendorous" (02_planet_physical_descriptions) = wheatish (same convention as Gemini-green/Sag-golden)
  saturn:  { build: new Set(["lean"]), complexion: new Set(["dark"]), height: new Set(["tall"]), teeth: new Set(["prominent"]), eyes: new Set(["small_deep"]) }, // EMACIATED, DARK, LARGE TEETH, "lean and TALL", red DEPRESSED/sunken eyes
  rahu:    { complexion: new Set(["dark"]) },                                                                                                    // smoky/dark
  ketu:    { complexion: new Set(["dark"]) },                                                                                                    // shadowy/dark
};

const _PLANET_LAGNA_CREDIT = 0.85; // credit when a lagna planet supports the answer

// Vol_06 §2.2 is explicit that appearance is NOT read from the rising sign alone: the body also
// resembles the Lagna LORD, the Moon sign (read in parallel — the Chandra Lagna), the Navamsa
// Lagna lord, and the single strongest planet overall. So the score is a multi-factor SHORTLIST,
// not a "looks like Scorpio ⇒ Scorpio rising" verdict. The Ascendant sign + planets in the
// Ascendant stay the PRIMARY signature (they carry the rectification signal — the Navamsa lagna
// even shifts every ~13 min); these four secondary factors are blended in at a modest weight so
// they refine and cross-check the ascendant without overruling it.
export const APPEARANCE_SECONDARY_WEIGHT = 0.25;
/** A sign-only (Ascendant-checklist) baseline below this is flagged "weak" — a chart that fails
 * the rising-sign cliché but is still valid (the Cancer-lagna-that-doesn't-look-Cancer case). */
export const SIGN_ONLY_WEAK_BELOW = 50.0;

/**
 * 0..1 weighted match of the answered questions to one zodiac sign's signature.
 *
 * Identical weighting to the primary rising-sign score, so it can be reused for any sign that
 * acts like a second lagna (e.g. the Moon sign / Chandra Lagna). null if nothing is answered.
 * `influence` is the per-item influence map — applied here exactly as on the primary path
 * (_opt_support), so a dialed-down trait can't count full-strength through the Moon-sign
 * cross-check while counting reduced against the Ascendant (fixed 2026-07-20).
 */
function _sign_signature_match(
  clean: Record<string, string[]>,
  sign: number,
  influence: Record<string, Record<string, string>> = {},
): number | null {
  let total = 0.0;
  let max_weight = 0.0;
  for (const question of TRAIT_QUESTIONS) {
    const selected = clean[question.key];
    if (!selected || selected.length === 0) continue;
    // Chart-scored circumstance questions carry no per-sign table — skip (see CHART_CIRCUMSTANCE_KEYS).
    if (CHART_CIRCUMSTANCE_KEYS.has(question.key)) continue;
    const weight = question.weight;
    max_weight += weight;
    let support = 0.0;
    for (const opt of selected) support += _opt_support(question, opt, sign, influence);
    const sign_score = Math.min(1.0, support);
    total += weight * sign_score;
  }
  return max_weight > 0 ? total / max_weight : null;
}

/**
 * 0..1 match of the answers to a graha's physical signature (Vol_06 §2.3 planet-in-lagna
 * table). Over the answered questions the planet HAS a signature for, the fraction where a
 * selected option matches. null if the planet has no signature among the answered questions.
 */
function _planet_signature_match(clean: Record<string, string[]>, planet: string | null): number | null {
  const sig_map = PLANET_LAGNA_TRAITS[(planet || "").toLowerCase()] ?? {};
  if (Object.keys(sig_map).length === 0) return null;
  let answered = 0;
  let hits = 0;
  for (const question of TRAIT_QUESTIONS) {
    const selected = clean[question.key];
    if (!selected || selected.length === 0) continue;
    const supported = sig_map[question.key];
    if (!supported || supported.size === 0) continue;
    answered += 1;
    if (selected.some((opt) => supported.has(opt))) hits += 1;
  }
  return answered > 0 ? hits / answered : null;
}

/**
 * The graha with the highest Shadbala-lite total — Vol_06's 'strongest planet overall',
 * which the body may resemble.
 */
function _strongest_graha(natal_chart: any): string | null {
  const shadbala = natal_chart?.shadbala || {};
  let best: [number, string] | null = null;
  for (const graha of _GRAHAS) {
    const sb = shadbala[graha];
    if (sb === null || sb === undefined) continue;
    const total = Number(sb?.total ?? 0.0);
    if (best === null || total > best[0]) {
      best = [total, graha];
    }
  }
  return best ? best[1] : null;
}

/**
 * Vol_06 multi-factor appearance cross-check — the MODIFIERS that make the scorer chart-based,
 * not sign-only. A weighted 0..100 match across:
 *   - the Lagna lord (weighted by its CONDITION — a strong lord stamps the body more cleanly),
 *   - the Lagna NAKSHATRA lord (the planet ruling the rising nakshatra),
 *   - the Navamsa Lagna lord, the Moon sign (Chandra Lagna), the strongest planet,
 *   - the planets ASPECTING the 1st house (their drishti colours the body too).
 * null if none yields a usable match. Returns the blended modifier score + a detail block that
 * still carries the four original keys (lagna_lord / navamsa_lagna_lord / strongest_planet /
 * moon_sign) plus the new modifier factors and the lagna-lord condition.
 */
function _secondary_appearance_match(
  natal_chart: any,
  clean: Record<string, string[]>,
  influence: Record<string, Record<string, string>> = {},
): [number | null, Record<string, any>] {
  const asc_sign = Math.trunc(Number(natal_chart.planets["ascendant"].sign));
  const lagna_lord = SIGN_RULERS[asc_sign];
  const navamsa_lagna_sign = _navamsa_sign(natal_chart.ascendant_longitude);
  const navamsa_lord = SIGN_RULERS[Math.trunc(navamsa_lagna_sign)];
  const strongest = _strongest_graha(natal_chart);
  const moon = natal_chart.planets["moon"];
  const nak_lord = _nakshatra_lord(Number(natal_chart.ascendant_longitude));
  const aspecting = _planets_aspecting_first(natal_chart, asc_sign);
  const condition = _lagna_lord_condition(natal_chart, lagna_lord);

  // Aspecting-planets modifier: the mean signature match of the grahas casting drishti on the 1st.
  const aspecting_matches = aspecting
    .map((g) => _planet_signature_match(clean, g))
    .filter((v): v is number => v !== null);
  const aspecting_first = aspecting_matches.length
    ? aspecting_matches.reduce((a, b) => a + b, 0) / aspecting_matches.length
    : null;

  const factors: Record<string, number | null> = {
    lagna_lord: _planet_signature_match(clean, lagna_lord),
    lagna_nakshatra_lord: _planet_signature_match(clean, nak_lord),
    navamsa_lagna_lord: _planet_signature_match(clean, navamsa_lord),
    strongest_planet: strongest ? _planet_signature_match(clean, strongest) : null,
    moon_sign: moon !== null && moon !== undefined ? _sign_signature_match(clean, Math.trunc(Number(moon.sign)), influence) : null,
    aspecting_first: aspecting_first,
  };
  // The lagna lord's factor is weighted by its condition (0.5 baseline + condition, so an unknown
  // condition = weight 1.0); every other factor is equal-weight 1.0. This is a bounded, faithful
  // use of "lagna lord condition" — a strong lord's resemblance counts more, a weak one's less.
  const factor_weights: Record<string, number> = {
    lagna_lord: 0.5 + (condition ?? 0.5),
    lagna_nakshatra_lord: 1.0,
    navamsa_lagna_lord: 1.0,
    strongest_planet: 1.0,
    moon_sign: 1.0,
    aspecting_first: 1.0,
  };

  const factor_matches: Record<string, number | null> = {};
  let wsum = 0;
  let acc = 0;
  for (const [k, v] of Object.entries(factors)) {
    factor_matches[k] = v !== null ? pythonRound(100.0 * v, 1) : null;
    if (v !== null) {
      const w = factor_weights[k] ?? 1.0;
      acc += w * 100.0 * v;
      wsum += w;
    }
  }
  const detail: Record<string, any> = {
    lagna_lord,
    lagna_nakshatra_lord: nak_lord,
    navamsa_lagna_lord: navamsa_lord,
    strongest_planet: strongest,
    moon_sign: moon !== null && moon !== undefined ? SIGN_NAMES[Math.trunc(Number(moon.sign))] : null,
    aspecting_first: aspecting,
    lagna_lord_condition: condition !== null ? pythonRound(condition, 3) : null,
    factor_matches,
  };
  if (wsum <= 0) {
    return [null, detail];
  }
  return [acc / wsum, detail];
}

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Keep only known question keys mapped to a list of known option keys.
 *
 * Every question is multi-select. A legacy single-string answer is coerced to a
 * one-item list so old saved profiles keep working. Duplicates are dropped and order
 * is preserved. Questions with no valid selections are omitted.
 *
 * When age_years is given, age-inappropriate answers (age_blocked_options) are dropped
 * too — a defensive backstop so answers captured before the age gate existed (or via a
 * surface that ignored it) can't skew a child's rising-sign shortlist with adult-pattern
 * markers like balding.
 */
export function normalize_trait_answers(answers: any, age_years: number | null = null): Record<string, string[]> {
  if (!_is_plain_object(answers)) return {};
  // Migrate any retired keys (standout/marks/temperament) to the current split dimensions first,
  // so old saved profiles keep scoring under the new taxonomy.
  answers = _apply_legacy_remap(answers as Record<string, any>);
  const valid_options: Record<string, Set<string>> = {};
  for (const q of TRAIT_QUESTIONS) valid_options[q.key] = new Set(Object.keys(q.options));
  const blocked = age_blocked_options(age_years);
  const clean: Record<string, string[]> = {};
  for (const [key, rawValue] of Object.entries(answers)) {
    if (!(key in valid_options)) continue;
    const bad = blocked[key] ?? new Set<string>();
    if (bad.has("*")) continue;
    let value: any = rawValue;
    if (typeof value === "string") value = [value];
    if (!Array.isArray(value)) continue;
    const seen = new Set<string>();
    const options: string[] = [];
    for (const v of value) {
      if (typeof v === "string" && valid_options[key].has(v) && !bad.has(v) && !seen.has(v)) {
        seen.add(v);
        options.push(v);
      }
    }
    if (options.length > 0) clean[key] = options;
  }
  return clean;
}

// ── Rejected traits — ABSOLUTE GATES ─────────────────────────────────────────────────────
// The user can state traits the person definitely does NOT have ("rejected traits"). These are
// gates, not soft votes (same design language as the strength/Ashtakavarga gates): each rejected
// option MULTIPLIES down every rising sign it indicates, in proportion to how strongly it
// indicates it. A sign whose primary signature the person definitely lacks keeps only
// (1 - REJECTED_TRAIT_PENALTY) of its trait score — strong enough to act as an exclusion, but
// never a hard zero (the classical signatures are stereotypes, so absolute certainty is never
// honest). Rejections ride along INSIDE the answers object under REJECTED_TRAITS_KEY so the
// score_candidate_record signature (kept Python-diffable) does not change; every existing
// consumer that normalizes answers simply ignores the reserved key.

export const REJECTED_TRAITS_KEY = "_rejected";
export const REJECTED_TRAIT_PENALTY = 0.9;
/** With ONLY rejections (no positive traits), unaffected signs read as this neutral score. */
export const REJECTED_ONLY_NEUTRAL_SCORE = 50.0;

// Per-rejection PENALTY OVERRIDE (2026-07-20). AUTO-DERIVED rejections (today only the
// delivery-type complement embedded by combined_trait_answers) are softer than a rejection the
// user explicitly stated: the user never said "definitely NOT feet-first" — the engine inferred
// it from a single, often hazy, family-lore answer. Such entries carry a per-option penalty
// under this reserved key inside the rejected map ({question_key: {option: penalty}}); options
// without an override keep the standard REJECTED_TRAIT_PENALTY. The key is reserved like
// _rejected/_influence: normalize_trait_answers drops it automatically (unknown question key),
// so only rejected_trait_sign_gates ever reads it.
export const REJECTED_PENALTY_OVERRIDE_KEY = "_penalty";

/** The per-option penalty override map embedded in a rejected-traits object (or {}). */
export function embedded_penalty_overrides(rejected: any): Record<string, Record<string, number>> {
  if (!_is_plain_object(rejected)) return {};
  const overrides = (rejected as Record<string, any>)[REJECTED_PENALTY_OVERRIDE_KEY];
  return _is_plain_object(overrides) ? overrides : {};
}

/** The rejected-traits map embedded in an answers object (or {}), without the positive keys. */
export function embedded_rejected_traits(answers: any): Record<string, any> {
  if (!_is_plain_object(answers)) return {};
  const rejected = (answers as Record<string, any>)[REJECTED_TRAITS_KEY];
  return _is_plain_object(rejected) ? rejected : {};
}

/**
 * Per-sign gate multipliers (length 12, each 0..1) from the rejected traits. For each question
 * with rejected options, a sign loses REJECTED_TRAIT_PENALTY × (its support for those options,
 * capped at 1). Gates from several questions compound multiplicatively — rejecting two of a
 * sign's primary markers excludes it almost entirely. All 1.0 when nothing is rejected.
 *
 * Options carrying a penalty OVERRIDE (REJECTED_PENALTY_OVERRIDE_KEY — the auto-derived
 * delivery-type rejections) gate at their own, milder penalty; they are grouped per penalty
 * value so user-stated rejections in the same question keep the exact standard math.
 */
export function rejected_trait_sign_gates(
  rejected: any,
  age_years: number | null = null,
  target: TraitTarget | null = null,
): number[] {
  const gates = new Array<number>(12).fill(1.0);
  const clean = normalize_trait_answers(rejected, age_years);
  const overrides = embedded_penalty_overrides(rejected);
  for (const question of TRAIT_QUESTIONS) {
    // When a target is given, only that target's questions gate (physical rejections gate the
    // rising sign; disposition rejections gate the Moon sign — they are never mixed).
    if (target !== null && question.target !== target) continue;
    const selected = clean[question.key];
    if (!selected || selected.length === 0) continue;
    // Group the rejected options by their effective penalty. Without overrides this is one
    // group at REJECTED_TRAIT_PENALTY — byte-identical to the original single-pass math.
    const by_penalty = new Map<number, string[]>();
    for (const opt of selected) {
      const raw = overrides[question.key]?.[opt];
      const penalty =
        typeof raw === "number" && raw > 0 && raw <= 1 ? raw : REJECTED_TRAIT_PENALTY;
      const group = by_penalty.get(penalty);
      if (group) group.push(opt);
      else by_penalty.set(penalty, [opt]);
    }
    for (const [penalty, opts] of by_penalty) {
      for (let sign = 0; sign < 12; sign++) {
        let support = 0.0;
        for (const opt of opts) support += question.options[opt].signs[sign] ?? 0.0;
        gates[sign] *= 1.0 - penalty * Math.min(1.0, support);
      }
    }
  }
  return gates;
}

// ── Per-item INFLUENCE (minimal/low/medium/high) ─────────────────────────────────────────────
// The user can dial how much an individual saved trait OPTION contributes to its group's score.
// A factor multiplies that option's per-sign support before the per-question cap, so e.g. a
// `medium` trait counts half. Defaults to `high` (1.0) so nothing changes until deliberately
// lowered. Rides along inside the answers object under INFLUENCE_KEY (same pattern as _rejected),
// so the score_candidate_record signature is unchanged. Shape: {question_key: {option: level}}.
export const INFLUENCE_KEY = "_influence";
export type InfluenceLevel = "minimal" | "low" | "medium" | "high";
export const INFLUENCE_FACTORS: Record<InfluenceLevel, number> = { minimal: 0.1, low: 0.25, medium: 0.5, high: 1.0 };
export const DEFAULT_INFLUENCE: InfluenceLevel = "high";

/** The 0..1 factor for an influence level (unknown/absent → high = 1.0). */
export function influence_factor(level: any): number {
  return INFLUENCE_FACTORS[level as InfluenceLevel] ?? INFLUENCE_FACTORS[DEFAULT_INFLUENCE];
}

/** The influence map embedded in an answers object (or {}). {question_key: {option: level}}. */
export function embedded_influence(answers: any): Record<string, Record<string, string>> {
  if (!_is_plain_object(answers)) return {};
  const inf = (answers as Record<string, any>)[INFLUENCE_KEY];
  return _is_plain_object(inf) ? inf : {};
}

// ── Embedded age (2026-07-20) ────────────────────────────────────────────────────────────────
// score_traits_for_candidate receives the person's age explicitly and drops age-inappropriate
// answers/rejections, but the chart-free ranking surfaces (rising_sign_from_traits etc., fed by
// combined_trait_answers) had no age channel and passed age=null to the same gates — so the two
// surfaces could RANK DIFFERENTLY for a child (e.g. a "receding hairline" rejection gated the
// heatmap ranking but not the candidate score). combined_trait_answers now embeds the profile's
// age under this reserved key (same ride-along pattern as _rejected/_influence — unknown keys
// are stripped by normalize_trait_answers), and _target_scores applies it to BOTH the positive
// answers and the rejection gates, so every surface age-gates identically.
export const AGE_KEY = "_age_years";

/** The age (years) embedded in an answers object, or null when absent/invalid. */
export function embedded_age_years(answers: any): number | null {
  if (!_is_plain_object(answers)) return null;
  const age = (answers as Record<string, any>)[AGE_KEY];
  return typeof age === "number" && Number.isFinite(age) && age >= 0 ? age : null;
}

// ── Gender (2026-07-20 — 06_sex_determination_multiple_births 'Male/Female Birth — Planetary
// Indicators') ──────────────────────────────────────────────────────────────────────────────
// "Strong Ascendant, Moon, Jupiter, and Sun all in odd signs AND odd Navamsas → male";
// "…all in even signs AND even Navamsas → female". A classical COARSE filter (RULES_TRACE listed
// it blocked on a gender field): the profile's optional `gender` field rides along under this
// reserved key (same pattern as _age_years/_rejected) and score_traits_for_candidate turns the
// 4-body sign+navamsa parity agreement into a BOUNDED multiplier (never a veto). The NAVAMSA
// parity flips every ~13 min, so even this coarse rule carries some intra-sign signal. Only
// "male"/"female" are ever embedded — other / prefer-not-to-say / absent are a no-op (the field
// is a classical-rule input only, not an identity statement).
export const GENDER_KEY = "_gender";
/** Multiplier bounds for the parity check: full 8/8 agreement ×1.05 … full disagreement ×0.9. */
export const GENDER_PARITY_MAX = 1.05;
export const GENDER_PARITY_MIN = 0.9;
const _GENDER_PARITY_BODIES = ["ascendant", "moon", "jupiter", "sun"] as const;

/** The classical-rule gender ("male"/"female") embedded in an answers object, else null. */
export function embedded_gender(answers: any): "male" | "female" | null {
  if (!_is_plain_object(answers)) return null;
  const g = String((answers as Record<string, any>)[GENDER_KEY] ?? "").toLowerCase();
  return g === "male" || g === "female" ? g : null;
}

/** Normalize a profile gender value to the classical-rule input ("male"/"female") or null. */
function _normalized_gender(raw: any): "male" | "female" | null {
  const g = String(raw ?? "").toLowerCase().trim();
  return g === "male" || g === "female" ? g : null;
}

// ── Birth-circumstance chart checks (2026-07-20) ─────────────────────────────────────────────
// The CHART side of the CHART_CIRCUMSTANCE_KEYS questions: deterministic per-candidate checks
// from 06_birth_circumstances + 12_gandanta_births, each a small bounded multiplier on the
// appearance/trait bucket. Design rules (matching the delivery_type gate's temperament):
//   • BOUNDED, never a veto — the strongest single credit is ×1.08, the strongest single miss
//     ×0.95, and the compounded product is clamped to CIRCUMSTANCE_MULT_(FLOOR|CEIL).
//   • `unknown`-style options and unanswered questions are exact no-ops (multiplier 1.0), so an
//     unanswered profile scores byte-identically to before this feature existed.
//   • Missing chart data (no Moon, no planet longitudes on synthetic stubs) degrades gracefully
//     to a no-op for the affected check.

// Sign-modality sets (01_signs_classification): dual/common {Gemini, Virgo, Sagittarius, Pisces},
// fixed {Taurus, Leo, Scorpio, Aquarius}.
const _DUAL_SIGNS = new Set([GEMINI, VIRGO, SAGITTARIUS, PISCES]);
const _FIXED_SIGNS = new Set([TAURUS, LEO, SCORPIO, AQUARIUS]);

// Natural benefics/malefics for the Ease-of-Delivery drishti read (mirrors the engine's
// NATURAL_BENEFICS in chart_overview.ts — traits.ts stays import-free of the scoring cluster).
const _NATURAL_BENEFICS = new Set(["jupiter", "venus", "mercury", "moon"]);
const _NATURAL_MALEFICS = new Set(["sun", "mars", "saturn", "rahu", "ketu"]);

// Gandanta windows (12_gandanta_births; MIRRORS of special_points.ts's calibrated orbs so this
// cluster stays import-free of the concurrently-edited scoring files — RULES_TRACE #35/#69):
//   • the three water→fire junctions sit at 0° (Pisces/Aries = Revati/Ashvini), 120°
//     (Cancer/Leo = Aslesha/Magha) and 240° (Scorpio/Sag = Jyeshtha/Mula);
//   • LAGNA gandanta = the last/first half-ghatika of rising ≈ 3° of Ascendant arc (~12 min);
//   • MOON nakshatra gandanta = ±2 ghatikas of mean Moon motion ≈ 0.8° of Moon arc (~48 min).
const _GANDANTA_JUNCTIONS_DEG = [0.0, 120.0, 240.0];
export const LAGNA_GANDANTA_ORB_DEG_TRAITS = 3.0;
export const MOON_GANDANTA_ORB_DEG_TRAITS = 0.8;

/** Absolute distance (deg) from a longitude to the nearest water→fire junction. */
function _gandanta_distance(longitude: number): number {
  const lon = ((longitude % 360) + 360) % 360;
  let best = 360.0;
  for (const j of _GANDANTA_JUNCTIONS_DEG) {
    for (const jj of [j, j + 360.0]) best = Math.min(best, Math.abs(lon - jj));
  }
  return best;
}

// Per-check multipliers. Kept deliberately WEAK relative to the delivery-type gate (×0.5): these
// are hazier lore, and several can stack — hence the product clamp below.
export const CIRCUMSTANCE_CREDIT = 1.05;        // the doc's condition holds for this candidate
export const CIRCUMSTANCE_PARTIAL_CREDIT = 1.02; // condition holds but contested (partial protection)
export const CIRCUMSTANCE_MISS = 0.97;          // condition fails (mild — lore is soft evidence)
export const CIRCUMSTANCE_CONTRA = 0.95;        // an explicit doc CONTRA-indication (vargottama vs travelling)
export const VARGOTTAMA_HOME_CREDIT = 1.04;     // additional credit: home birth + Vargottama lagna
export const GANDANTA_LORE_CREDIT = 1.08;       // remedy lore + chart in a gandanta window (sharp band)
export const GANDANTA_LORE_MISS = 0.97;         // remedy lore but no gandanta anywhere in this chart
export const NO_LORE_CREDIT = 1.02;             // "no such thing" + non-gandanta (VERY weak evidence)
export const NO_LORE_GANDANTA = 0.99;           // "no such thing" + gandanta (near no-op)
/** The compounded circumstance × gender product is clamped to this band — never a veto. */
export const CIRCUMSTANCE_MULT_FLOOR = 0.85;
export const CIRCUMSTANCE_MULT_CEIL = 1.15;

/**
 * The bounded birth-circumstance × gender-parity multiplier for one candidate chart, with a
 * per-check detail block. Returns [1.0, {}]-equivalent (multiplier exactly 1.0) whenever nothing
 * relevant is answered — the byte-identical no-op guarantee.
 */
function _circumstance_multiplier(
  natal_chart: any,
  clean: Record<string, string[]>,
  gender: "male" | "female" | null,
): [number, Record<string, any>] {
  const detail: Record<string, any> = {};
  let mult = 1.0;
  const asc = natal_chart?.planets?.["ascendant"];
  if (asc === null || asc === undefined) return [1.0, detail];
  const asc_sign = Math.trunc(Number(asc.sign));
  const asc_lon = Number(natal_chart?.ascendant_longitude ?? NaN);
  const navamsa_sign = Number.isNaN(asc_lon) ? null : _navamsa_sign(asc_lon);
  const vargottama = navamsa_sign !== null && navamsa_sign === asc_sign;

  // ── birth_place_kind (06_birth_circumstances 'Place of Delivery — …') ──
  const place = clean[BIRTH_PLACE_KEY]?.length === 1 ? clean[BIRTH_PLACE_KEY][0] : null;
  if (place === "travelling") {
    // Dual D1 lagna OR dual NAVAMSA lagna → "delivery occurred on the way"; the doc's stated
    // exception: a Vargottama lagna OVERRIDES this and indicates delivery at home.
    const dual = _DUAL_SIGNS.has(asc_sign) || (navamsa_sign !== null && _DUAL_SIGNS.has(navamsa_sign));
    const m = vargottama ? CIRCUMSTANCE_CONTRA : dual ? CIRCUMSTANCE_CREDIT : CIRCUMSTANCE_MISS;
    mult *= m;
    detail[BIRTH_PLACE_KEY] = { answer: place, dual_lagna_or_navamsa: dual, vargottama, multiplier: m };
  } else if (place === "own_home" || place === "parents_home") {
    // Fixed D1/D9 lagna → "one's own established place" (credited for OWN home only — the doc's
    // fixed-sign reading is specifically one's own place); Vargottama → "one's own house
    // specifically", credited for EITHER home option as the strongest home-birth indicator.
    let m = 1.0;
    const fixed = _FIXED_SIGNS.has(asc_sign) || (navamsa_sign !== null && _FIXED_SIGNS.has(navamsa_sign));
    if (place === "own_home") m *= fixed ? CIRCUMSTANCE_CREDIT : CIRCUMSTANCE_MISS;
    if (vargottama) m *= VARGOTTAMA_HOME_CREDIT;
    mult *= m;
    detail[BIRTH_PLACE_KEY] = { answer: place, fixed_lagna_or_navamsa: fixed, vargottama, multiplier: m };
  }
  // hospital / unknown / multi-select → neutral no-op.

  // ── delivery_ease (06_birth_circumstances 'Ease of Delivery' / 'Difficult Delivery') ──
  // Read via the same whole-sign drishti helper the appearance modifiers use, degrading to a
  // no-op when the chart carries no grahas (synthetic stubs). Benefic INFLUENCE counts drishti on
  // the 1st or a benefic occupying the lagna ("strong benefic in Ascendant may ease a breech
  // delivery"); malefic influence likewise counts aspect OR occupation.
  const ease = clean[DELIVERY_EASE_KEY]?.length === 1 ? clean[DELIVERY_EASE_KEY][0] : null;
  if (ease === "easy" || ease === "difficult") {
    const grahas_present = _GRAHAS.some((g) => natal_chart?.planets?.[g] !== null && natal_chart?.planets?.[g] !== undefined);
    if (grahas_present) {
      const aspecting = _planets_aspecting_first(natal_chart, asc_sign);
      const occupants = _GRAHAS.filter(
        (g) =>
          natal_chart?.planets?.[g] !== null &&
          natal_chart?.planets?.[g] !== undefined &&
          Math.trunc(Number(natal_chart.planets[g].sign)) === asc_sign,
      );
      const influence_on_first = [...aspecting, ...occupants];
      const benefic = influence_on_first.some((g) => _NATURAL_BENEFICS.has(g));
      const malefic = influence_on_first.some((g) => _NATURAL_MALEFICS.has(g));
      let m: number;
      if (ease === "easy") {
        // Benefic aspects on the Ascendant → easy; malefics also aspecting → "protection is partial".
        m = benefic ? (malefic ? CIRCUMSTANCE_PARTIAL_CREDIT : CIRCUMSTANCE_CREDIT) : CIRCUMSTANCE_MISS;
      } else {
        // Malefics on the Ascendant WITHOUT benefic protection → difficult; a benefic aspect
        // "cancels or reduces" → partial credit only; no malefic influence at all → miss.
        m = malefic ? (benefic ? CIRCUMSTANCE_PARTIAL_CREDIT : CIRCUMSTANCE_CREDIT) : CIRCUMSTANCE_MISS;
      }
      mult *= m;
      detail[DELIVERY_EASE_KEY] = {
        answer: ease,
        benefic_on_first: benefic,
        malefic_on_first: malefic,
        multiplier: m,
      };
    }
  }

  // ── father_absent_birth (06_birth_circumstances 'Father Absent at Birth') ──
  // "Ascendant unaspected by the Moon → father absent". Moon influence on the 1st = the Moon's
  // whole-sign drishti (7th) or the Moon occupying the lagna. Absence of the Moon in the chart
  // stub → no-op.
  const father = clean[FATHER_ABSENT_KEY]?.length === 1 ? clean[FATHER_ABSENT_KEY][0] : null;
  if (father === "father_absent" || father === "father_present") {
    const moon = natal_chart?.planets?.["moon"];
    if (moon !== null && moon !== undefined) {
      const moon_sign = Math.trunc(Number(moon.sign));
      const off = (((asc_sign - moon_sign) % 12) + 12) % 12;
      const moon_on_first = moon_sign === asc_sign || _ASPECT_OFFSETS["moon"].has(off);
      let m = 1.0;
      if (father === "father_absent") {
        m = moon_on_first ? CIRCUMSTANCE_MISS : CIRCUMSTANCE_CREDIT;
      } else if (moon_on_first) {
        // Father present is the base rate — only a MILD credit when the Moon does aspect the
        // lagna, and NO penalty otherwise (weak one-sided evidence).
        m = CIRCUMSTANCE_PARTIAL_CREDIT;
      }
      mult *= m;
      detail[FATHER_ABSENT_KEY] = { answer: father, moon_on_first, multiplier: m };
    }
  }

  // ── birth_remedy_lore (12_gandanta_births) ──
  // Affirmed remedy lore (a post-birth puja/charity, or the father's viewing restriction) points
  // INTO a gandanta window — lagna gandanta (Ascendant within ~3° of a water→fire junction) or
  // Moon nakshatra gandanta (Moon within ~0.8° of the same junction degrees). "No such thing" is
  // only VERY weak evidence of a non-gandanta birth.
  const lore = clean[REMEDY_LORE_KEY]?.length === 1 ? clean[REMEDY_LORE_KEY][0] : null;
  if (lore === "remedy_performed" || lore === "father_avoided" || lore === "none_unknown") {
    const lagna_g = !Number.isNaN(asc_lon) && _gandanta_distance(asc_lon) <= LAGNA_GANDANTA_ORB_DEG_TRAITS;
    const moon_lon = Number(natal_chart?.planets?.["moon"]?.longitude ?? NaN);
    const moon_g = !Number.isNaN(moon_lon) && _gandanta_distance(moon_lon) <= MOON_GANDANTA_ORB_DEG_TRAITS;
    const in_gandanta = lagna_g || moon_g;
    let m: number;
    if (lore === "none_unknown") {
      m = in_gandanta ? NO_LORE_GANDANTA : NO_LORE_CREDIT;
    } else {
      m = in_gandanta ? GANDANTA_LORE_CREDIT : GANDANTA_LORE_MISS;
    }
    mult *= m;
    detail[REMEDY_LORE_KEY] = { answer: lore, lagna_gandanta: lagna_g, moon_gandanta: moon_g, multiplier: m };
  }

  // ── gender parity (06_sex_determination 'Male/Female Birth — Planetary Indicators') ──
  // Asc/Moon/Jupiter/Sun in odd signs AND odd navamsas → male; even/even → female. Counted as up
  // to 8 checks (4 bodies × D1 parity + D9 parity where a longitude is available; odd sign =
  // even 0-based index: Aries, Gemini, Leo, Libra, Sagittarius, Aquarius). The agreement fraction
  // maps linearly onto ×GENDER_PARITY_MIN..×GENDER_PARITY_MAX — a weak coarse filter, per the doc's
  // own exception note ("even one key planet … shifts the indicator").
  if (gender !== null) {
    let checks = 0;
    let matches = 0;
    const want_odd = gender === "male";
    for (const body of _GENDER_PARITY_BODIES) {
      const p = body === "ascendant" ? asc : natal_chart?.planets?.[body];
      if (p === null || p === undefined) continue;
      const sign = Math.trunc(Number(p.sign));
      checks += 1;
      if ((sign % 2 === 0) === want_odd) matches += 1;
      const lon = body === "ascendant" ? asc_lon : Number(p?.longitude ?? NaN);
      if (!Number.isNaN(lon)) {
        const d9 = _navamsa_sign(lon);
        checks += 1;
        if ((d9 % 2 === 0) === want_odd) matches += 1;
      }
    }
    if (checks > 0) {
      const frac = matches / checks;
      const m = GENDER_PARITY_MIN + (GENDER_PARITY_MAX - GENDER_PARITY_MIN) * frac;
      mult *= m;
      detail["gender_parity"] = { gender, checks, matches, multiplier: pythonRound(m, 4) };
    }
  }

  // Clamp the compounded product — bounded by construction AND by contract (never a veto).
  mult = Math.max(CIRCUMSTANCE_MULT_FLOOR, Math.min(CIRCUMSTANCE_MULT_CEIL, mult));
  return [mult, detail];
}

/** Option support for a sign, scaled by that option's influence factor (default high = full). */
function _opt_support(question: TraitQuestion, opt: string, sign: number, influence: Record<string, Record<string, string>>): number {
  const base = question.options[opt]?.signs[sign] ?? 0.0;
  if (base === 0) return 0.0;
  return base * influence_factor(influence[question.key]?.[opt]);
}

// ── Delivery-type ELIMINATION (Rectification Step 6) ─────────────────────────────────────
// 06_chart_rectification Step 6 frames delivery type as "a binary confirmation that eliminates
// half the candidate signs": a head-first birth doesn't just SUPPORT the six Sirshodaya signs —
// it RULES OUT the Prishtodaya five and Pisces. Positive per-sign support alone only adds
// proportional credit, so the elimination half is implemented here: when the profile asserts
// exactly ONE delivery_type option, the OTHER delivery_type options are auto-embedded into the
// rejected-traits map that rides along under REJECTED_TRAITS_KEY.
//
// BOUNDED GATE (2026-07-20 recalibration). The auto-embedded rejections used to gate every
// non-matching sign at the full REJECTED_TRAIT_PENALTY (×0.1) — and because the SAME single
// answer also adds its full 0.8-weight positive support, one hazy family-lore item both boosted
// the matching class AND crushed every other sign's whole trait bucket to ~10%. That double
// count let the weakest kind of evidence (second-hand delivery lore) dominate stronger direct
// observations. The gate is kept (Step 6's elimination IS the point) but the AUTO-derived
// rejections now carry a per-rejection penalty override of DELIVERY_AUTO_REJECT_PENALTY (×0.5
// per non-matching sign) via REJECTED_PENALTY_OVERRIDE_KEY: still a real, class-wide exclusion
// pressure ON TOP of the missing positive support, but bounded so one answer can no longer
// single-handedly write off a sign. A delivery option the user EXPLICITLY rejects (real
// rejected_traits entry) keeps the full standard penalty — only the inferred complement is
// softened. If the user asserts TWO OR MORE delivery types (contradictory input), nothing is
// auto-rejected — the answers still add their positive support, but no class is eliminated.

export const DELIVERY_TYPE_KEY = "delivery_type";
/** Milder gate for AUTO-derived (delivery-complement) rejections — see the block comment. */
export const DELIVERY_AUTO_REJECT_PENALTY = 0.5;

/** The delivery_type options to auto-reject given the asserted traits, or null when the profile
 *  asserts anything other than exactly one delivery_type option (unknown or contradictory).
 *  `rejected` is the profile's REAL rejected-traits map: when the asserted option is itself
 *  really-rejected (contradictory/dirty input), the standing convention is that the rejection
 *  wins — so no complement is auto-rejected (otherwise all three options would end up rejected
 *  and the asserted class would be suppressed too; caught by the 2026-07-02 review). */
function _delivery_auto_rejections(traits: any, rejected: any): string[] | null {
  const selected = normalize_trait_answers(traits)[DELIVERY_TYPE_KEY] ?? [];
  if (selected.length !== 1) return null; // unknown, or a contradictory double-selection
  const really_rejected = normalize_trait_answers(rejected)[DELIVERY_TYPE_KEY] ?? [];
  if (really_rejected.includes(selected[0])) return null; // asserted AND rejected → rejection wins
  const question = TRAIT_QUESTIONS.find((q) => q.key === DELIVERY_TYPE_KEY);
  if (!question) return null;
  return Object.keys(question.options).filter((opt) => opt !== selected[0]);
}

/**
 * The profile's trait answers with its rejected traits (under REJECTED_TRAITS_KEY) and per-item
 * influence (under INFLUENCE_KEY) embedded — the single way callers thread both into the scorers.
 *
 * When the profile asserts exactly one delivery_type, the OTHER delivery types are auto-embedded
 * into the rejected map (merged over any real rejections, never mutating the profile) so the
 * Step 6 elimination gate fires in every consumer — see the comment block above.
 */
export function combined_trait_answers(user_profile: Record<string, any> | null | undefined): Record<string, any> {
  const traits = _is_plain_object(user_profile?.["traits"]) ? user_profile!["traits"] : {};
  const rejected = _is_plain_object(user_profile?.["rejected_traits"]) ? user_profile!["rejected_traits"] : {};
  const influence = _is_plain_object(user_profile?.["trait_influence"]) ? user_profile!["trait_influence"] : {};
  const delivery_rejections = _delivery_auto_rejections(traits, rejected);
  const hasRejected = Object.keys(normalize_trait_answers(rejected)).length > 0;
  const hasInfluence = Object.keys(influence).length > 0;
  // The profile's age rides along under AGE_KEY so the chart-free ranking surfaces age-gate
  // exactly like score_traits_for_candidate (which receives the age explicitly) — see AGE_KEY.
  const age_years = age_years_from_birth(user_profile?.["birth"]?.["date"] ?? null);
  // The optional profile gender rides along under GENDER_KEY (classical parity check input —
  // see the GENDER_KEY block). Only "male"/"female" embed; other/prefer-not/absent stay a no-op.
  const gender = _normalized_gender(user_profile?.["gender"]);
  if (!hasRejected && !hasInfluence && delivery_rejections === null && age_years === null && gender === null)
    return traits;
  const out: Record<string, any> = { ...traits };
  if (delivery_rejections !== null) {
    // Merge the auto-rejections with any REAL rejected delivery options (deduped), on a copy —
    // the profile's own rejected_traits object is never mutated.
    const prior = (rejected as Record<string, any>)[DELIVERY_TYPE_KEY];
    const prior_list: string[] = typeof prior === "string" ? [prior] : Array.isArray(prior) ? prior : [];
    const auto_only = delivery_rejections.filter((opt) => !prior_list.includes(opt));
    // ONLY the auto-derived options carry the milder penalty override; a delivery option the
    // user really rejected (prior_list) keeps the full REJECTED_TRAIT_PENALTY.
    const prior_overrides = embedded_penalty_overrides(rejected);
    out[REJECTED_TRAITS_KEY] = {
      ...rejected,
      [DELIVERY_TYPE_KEY]: [...prior_list, ...auto_only],
      ...(auto_only.length > 0
        ? {
            [REJECTED_PENALTY_OVERRIDE_KEY]: {
              ...prior_overrides,
              [DELIVERY_TYPE_KEY]: {
                ...(prior_overrides[DELIVERY_TYPE_KEY] ?? {}),
                ...Object.fromEntries(auto_only.map((opt) => [opt, DELIVERY_AUTO_REJECT_PENALTY])),
              },
            },
          }
        : {}),
    };
  } else if (hasRejected) {
    out[REJECTED_TRAITS_KEY] = rejected;
  }
  if (hasInfluence) out[INFLUENCE_KEY] = influence;
  if (age_years !== null) out[AGE_KEY] = age_years;
  if (gender !== null) out[GENDER_KEY] = gender;
  return out;
}

/**
 * Render saved trait answers as plain question/answer pairs ({"question", "selected"}),
 * where `selected` is the list of chosen option labels.
 */
export function trait_answers_as_qa(answers: any): Record<string, any>[] {
  const clean = normalize_trait_answers(answers);
  if (Object.keys(clean).length === 0) return [];
  const by_key: Record<string, TraitQuestion> = {};
  for (const q of TRAIT_QUESTIONS) by_key[q.key] = q;
  const pairs: Record<string, any>[] = [];
  for (const [key, options] of Object.entries(clean)) {
    const question = by_key[key];
    if (!question) continue;
    const labels = options.map((opt) => question.options[opt]?.label ?? opt);
    pairs.push({
      question: question.prompt,
      selected: labels,
      source: "appearance_temperament",
    });
  }
  return pairs;
}

/**
 * Which rising sign(s) the chosen traits point to — independent of any chart.
 *
 * Uses ONLY the physical (lagna-targeted) questions: appearance is what maps to the rising sign
 * (Vol_06 §2.1-2.3). Disposition questions are scored against the MOON sign elsewhere and contribute
 * nothing here — ranking the rising sign from personality would be the Moon-vs-Lagna confusion.
 * Sums each selected physical option's per-sign support (capped per question, weighted), returning
 * every supported sign as (sign_name, 0-100 match%) ranked high to low, with the same weights the
 * chart scorer uses so it agrees with the ranking.
 */
/**
 * Generic per-target sign ranking from the chosen traits, independent of any chart. Sums each
 * selected option's per-sign support (scaled by per-item influence, capped per question, weighted),
 * applies that target's rejected-trait gates, and returns every supported sign as (sign_name,
 * 0-100 match%) ranked high to low.
 *   • target "lagna" → APPEARANCE: maps to the rising sign (Vol_06 §2.1-2.3).
 *   • target "moon"  → PERSONALITY: maps to the Moon sign (Vol_06 §3.1, Vol_11 §7.1).
 * The two are NEVER mixed — disposition can't rank the rising sign (the Moon-vs-Lagna confusion).
 */
/**
 * Per-sign 0-100 score for one target (length-12 array), or null when nothing in that target is
 * answered or rejected. Unlike the ranking, this keeps 0 values (a sign the answered traits don't
 * match) so callers can read a definite "0%, not no-data" for any sign.
 */
function _target_scores(answers: any, target: TraitTarget): number[] | null {
  // The embedded age (AGE_KEY, threaded by combined_trait_answers) applies to BOTH the positive
  // answers and the rejection gates — previously the gates here ran age-blind (age=null) while
  // score_traits_for_candidate gated with the real age, so the two surfaces could rank child
  // profiles differently (fixed 2026-07-20).
  const age_years = embedded_age_years(answers);
  const clean = normalize_trait_answers(answers, age_years);
  const rejected = embedded_rejected_traits(answers);
  const influence = embedded_influence(answers);
  // Chart-scored circumstance questions are NEUTRAL on the chart-free sign surfaces: their
  // classical conditions (dual/fixed NAVAMSA lagna, drishti on the 1st, gandanta windows) need a
  // chart and discriminate WITHIN a sign, so they contribute nothing to a per-sign ranking.
  const questions = TRAIT_QUESTIONS.filter((q) => q.target === target && !CHART_CIRCUMSTANCE_KEYS.has(q.key));
  const gates = rejected_trait_sign_gates(rejected, age_years, target);
  const has_rejections = gates.some((g) => g < 1.0);
  const answered = questions.filter((q) => (clean[q.key]?.length ?? 0) > 0);
  if (answered.length === 0 && !has_rejections) return null;

  const out = new Array<number>(12).fill(0);
  if (answered.length === 0) {
    // Rejections only: neutral × the exclusion gate.
    for (let sign = 0; sign < 12; sign++) out[sign] = pythonRound(REJECTED_ONLY_NEUTRAL_SCORE * gates[sign], 1);
    return out;
  }
  const scores: number[] = new Array<number>(12).fill(0);
  let max_weight = 0.0;
  for (const question of answered) {
    const selected = clean[question.key];
    max_weight += question.weight;
    for (let sign = 0; sign < 12; sign++) {
      let support = 0.0;
      for (const opt of selected) support += _opt_support(question, opt, sign, influence);
      scores[sign] += question.weight * Math.min(1.0, support);
    }
  }
  if (max_weight <= 0) return null;
  for (let sign = 0; sign < 12; sign++) out[sign] = pythonRound((100.0 * scores[sign] * gates[sign]) / max_weight, 1);
  return out;
}

function _sign_ranking_for_target(answers: any, target: TraitTarget): Array<[string, number]> {
  const scores = _target_scores(answers, target);
  if (!scores) return [];
  const ranked: Array<[string, number]> = scores.map((v, i) => [SIGN_NAMES[i], v] as [string, number]);
  ranked.sort((a, b) => b[1] - a[1]);
  return ranked.filter((item) => item[1] > 0);
}

/**
 * Which RISING sign(s) the chosen APPEARANCE (physical) traits point to — chart-free. The
 * appearance metric of the heatmap. Disposition contributes nothing (it's a Moon-sign signal).
 */
export function rising_sign_from_traits(answers: any): Array<[string, number]> {
  return _sign_ranking_for_target(answers, "lagna");
}

/**
 * Which MOON sign(s) the chosen PERSONALITY (modern-astrology) traits point to — chart-free. The
 * personality metric (WEAK, Barnum-prone). Used (indexed by the chart's actual Moon sign) for the
 * per-window value.
 */
export function personality_from_traits(answers: any): Array<[string, number]> {
  return _sign_ranking_for_target(answers, "moon");
}

/** The 0-100 personality (modern-astrology) match for ONE sign — the per-window Moon-sign value.
 *  WEAK evidence. Returns a definite number (incl. 0) when any personality dim is answered; null
 *  only when none is. */
export function personality_match_for_sign(answers: any, sign: number | null | undefined): number | null {
  if (sign === null || sign === undefined) return null;
  const scores = _target_scores(answers, "moon");
  return scores ? scores[Math.trunc(sign)] : null;
}

/**
 * For each ANSWERED appearance/temperament question, how well each sign matches the picks —
 * used to show, per rising sign, the highest match of each personality/physical trait. Returns
 * a list of {key, prompt, by_sign:{sign_name: 0-100}} for answered questions only.
 */
export function per_question_sign_match(answers: any): Record<string, any>[] {
  const clean = normalize_trait_answers(answers);
  const influence = embedded_influence(answers);
  const out: Record<string, any>[] = [];
  for (const question of TRAIT_QUESTIONS) {
    const selected = clean[question.key];
    if (!selected || selected.length === 0) continue;
    // Chart-scored circumstance questions have no per-sign signature — an all-zero row here
    // would misread as "matches no sign" (see CHART_CIRCUMSTANCE_KEYS).
    if (CHART_CIRCUMSTANCE_KEYS.has(question.key)) continue;
    const by_sign: Record<string, number> = {};
    for (let sign = 0; sign < 12; sign++) {
      let raw = 0.0;
      for (const opt of selected) raw += _opt_support(question, opt, sign, influence);
      const support = Math.min(1.0, raw);
      by_sign[SIGN_NAMES[sign]] = pythonRound(100.0 * support, 1);
    }
    // `target` tells the consumer which sign `by_sign` is about: "lagna" = the rising sign,
    // "moon" = the Moon sign (a disposition match, NOT a rising-sign signal).
    out.push({ key: question.key, prompt: question.prompt, target: question.target, by_sign });
  }
  return out;
}

// Short, human labels for each question — used when describing a sign's signature compactly.
const _QUESTION_SHORT_LABEL: Record<string, string> = {
  build: "build",
  height: "height",
  complexion: "complexion",
  face: "face",
  forehead: "forehead",
  eyes: "eyes",
  nose: "nose",
  hair: "hair",
  hairline: "hairline",
  teeth: "teeth",
  body_hair: "body hair",
  neck_shoulders: "neck/shoulders",
  limbs: "limbs",
  bearing: "bearing",
  delivery_type: "delivery",
  // birth circumstances (chart-scored birth facts; excluded from the appearance summary like delivery)
  birth_place_kind: "birth place",
  delivery_ease: "delivery ease",
  father_absent_birth: "father at birth",
  birth_remedy_lore: "birth remedy lore",
  // personality dims (modern astrology; Moon-targeted; excluded from the physical/rising-sign summary)
  psych_style: "psychology",
  emotional: "emotion",
  social_behaviour: "social",
  relationship: "relationships",
};
// A sign "owns" an option as part of its signature when its support for that option is at least
// this high — i.e. the option is a primary/strong indicator of the sign, not an incidental one.
const _SIGNATURE_OWN_THRESHOLD = 0.7;

/**
 * The classical appearance/temperament signature of one rising sign — the options that most
 * strongly indicate it — derived by INVERTING the same TRAIT_QUESTIONS tables the scorer uses,
 * so this reference can never drift from the ranking. Returns
 * {"sign", "by_question": {short_label: [option labels]}, "summary": "build: large; face: ..."}.
 *
 * Used to show, on hover/in a reference panel, "what each rising sign should look like" so a user
 * can see why their description did or didn't match — e.g. Leo = large build, broad face, regal
 * bearing, fairer complexion (which a sharp-faced, medium-built, wheatish description won't hit).
 */
export function sign_appearance_signature(sign: number | string, include_temperament = true): Record<string, any> {
  if (typeof sign === "string") {
    const index = SIGN_NAMES.indexOf(sign);
    if (index === -1) {
      return { sign, by_question: {}, summary: "" };
    }
    sign = index;
  }
  sign = Math.trunc(sign);
  const by_question: Record<string, string[]> = {};
  const summary_parts: string[] = [];
  for (const question of TRAIT_QUESTIONS) {
    // Moon-targeted disposition dims are gated by include_temperament and NEVER enter the physical
    // (rising-sign) appearance summary — that summary describes only the observable lagna body.
    // Non-visual lagna questions (delivery type — a birth FACT, not a body feature) are likewise
    // excluded: this signature feeds the "what the sign looks like" surfaces (leader blurb, hover
    // "Requires (appearance)", the signs reference), where a delivery entry would masquerade as a
    // §2.2/§2.3/§2.5 appearance requirement (fixed 2026-07-02).
    if (question.target === "lagna" && NON_VISUAL_QUESTION_KEYS.has(question.key)) continue;
    const is_personality = question.target === "moon";
    if (is_personality && !include_temperament) continue;
    const scored: Array<[number, string]> = Object.values(question.options).map((opt) => [
      opt.signs[sign as number] ?? 0.0,
      opt.label,
    ]);
    let owned = scored.filter(([support]) => support >= _SIGNATURE_OWN_THRESHOLD).map(([, label]) => label);
    if (owned.length === 0) {
      // Python max(..., key=...) keeps the FIRST maximal element.
      let best: [number, string | null] = [0.0, null];
      if (scored.length > 0) {
        best = scored[0];
        for (let i = 1; i < scored.length; i++) {
          if (scored[i][0] > best[0]) best = scored[i];
        }
      }
      if (best[0] > 0.0) owned = [best[1] as string];
    }
    if (owned.length > 0) {
      const short = _QUESTION_SHORT_LABEL[question.key] ?? question.key;
      by_question[short] = owned;
      if (!is_personality) summary_parts.push(`${short}: ${owned.join(" / ")}`);
    }
  }
  const summary = summary_parts.join("; ");
  return { sign: SIGN_NAMES[sign], by_question, summary };
}

/** sign_name -> sign_appearance_signature for all twelve signs. */
export function all_sign_signatures(include_temperament = true): Record<string, Record<string, any>> {
  const out: Record<string, Record<string, any>> = {};
  for (let s = 0; s < 12; s++) {
    out[SIGN_NAMES[s]] = sign_appearance_signature(s, include_temperament);
  }
  return out;
}

/**
 * Score how well a candidate chart matches the reported traits, by the source-correct split:
 *   • PHYSICAL questions (target "lagna") are matched to the ASCENDANT sign + the grahas occupying
 *     it (Vol_06 §2.1-2.3). This is the rising-sign discriminator — constant across the ~2h a sign
 *     rises, so it complements the event-dasha engine that fixes the dates.
 *   • DISPOSITION questions (target "moon") are matched to the candidate's MOON sign (Vol_06 §3.1,
 *     Vol_11 §7.1) — never the rising sign. The Moon barely moves within a day, so this is a small,
 *     near-constant contribution that only shifts on the ~40% of days the Moon changes sign.
 * Returns (0-100 or null if no usable answers, detail). age_years (when known) drops
 * age-inappropriate answers before scoring.
 */
export function score_traits_for_candidate(
  natal_chart: any,
  answers: any,
  age_years: number | null = null,
): [number | null, Record<string, any>] {
  const clean = normalize_trait_answers(answers, age_years);
  const rejected = embedded_rejected_traits(answers);
  const influence = embedded_influence(answers);
  const asc_sign = Math.trunc(Number(natal_chart.planets["ascendant"].sign));
  const moon = natal_chart.planets["moon"];
  const moon_sign = moon !== null && moon !== undefined ? Math.trunc(Number(moon.sign)) : null;
  // Physical rejections gate the rising sign; disposition rejections gate the Moon sign.
  const lagna_gates = rejected_trait_sign_gates(rejected, age_years, "lagna");
  const moon_gates = rejected_trait_sign_gates(rejected, age_years, "moon");
  const lagna_gate = lagna_gates[asc_sign];
  const moon_gate = moon_sign !== null ? moon_gates[moon_sign] : 1.0;
  const has_rejections = lagna_gates.some((g) => g < 1.0) || moon_gates.some((g) => g < 1.0);
  if (Object.keys(clean).length === 0 && !has_rejections) return [null, {}];
  // Birth-circumstance × gender-parity multiplier (bounded, chart-dependent — see
  // _circumstance_multiplier). Exactly 1.0 when nothing relevant is answered, so every legacy
  // profile scores byte-identically.
  const [circ_mult, circ_detail] = _circumstance_multiplier(natal_chart, clean, embedded_gender(answers));
  const circ_fields = Object.keys(circ_detail).length
    ? { circumstances: circ_detail, circumstance_multiplier: pythonRound(circ_mult, 4) }
    : {};
  if (Object.keys(clean).length === 0) {
    // Rejections only: start from neutral and apply both gates (rising-sign physical exclusion ×
    // Moon-sign disposition exclusion), so a candidate the person definitely doesn't match scores
    // low. The gender-parity multiplier (the only circumstance factor possible with no answers)
    // still applies — it is a chart-side modifier, not an answer.
    return [
      REJECTED_ONLY_NEUTRAL_SCORE * lagna_gate * moon_gate * circ_mult,
      {
        ascendant_sign: asc_sign,
        moon_sign,
        rejected_only: true,
        rejected_gate: pythonRound(lagna_gate * moon_gate, 3),
        // Physical-only neutral × the PHYSICAL gate alone (no Moon gate) for the appearance bucket.
        appearance_physical_score: pythonRound(REJECTED_ONLY_NEUTRAL_SCORE * lagna_gate * circ_mult, 2),
        rejected: normalize_trait_answers(rejected, age_years),
        ...circ_fields,
      },
    ];
  }
  const lagna_planets = _GRAHAS.filter(
    (name) =>
      natal_chart.planets[name] !== null &&
      natal_chart.planets[name] !== undefined &&
      Math.trunc(Number(natal_chart.planets[name].sign)) === asc_sign,
  );

  let total = 0.0;
  let max_weight = 0.0;
  let lagna_total = 0.0;
  let lagna_max_weight = 0.0;
  let moon_total = 0.0;
  let moon_max_weight = 0.0;
  const per_question: Record<string, any> = {};
  for (const question of TRAIT_QUESTIONS) {
    const selected = clean[question.key];
    if (!selected || selected.length === 0) continue;
    // Birth-circumstance questions are CHART-scored (bounded multipliers below), never additive —
    // their empty sign maps would otherwise dilute max_weight (see CHART_CIRCUMSTANCE_KEYS).
    if (CHART_CIRCUMSTANCE_KEYS.has(question.key)) continue;
    const weight = question.weight;
    if (question.target === "moon") {
      // Disposition → the Moon sign. If the chart has no Moon (synthetic stub), skip it entirely
      // rather than penalise — it simply doesn't contribute.
      if (moon_sign === null) continue;
      max_weight += weight;
      moon_max_weight += weight;
      let moon_score = 0.0;
      for (const opt of selected) moon_score += _opt_support(question, opt, moon_sign, influence);
      const contribution = Math.min(1.0, moon_score);
      total += weight * contribution;
      moon_total += weight * contribution;
      per_question[question.key] = { selected, target: "moon", moon_score: pythonRound(contribution, 2) };
      continue;
    }
    // Physical → the Ascendant sign, plus planet-in-lagna credit (Vol_06 §2.3).
    max_weight += weight;
    lagna_max_weight += weight;
    let sign_score = 0.0;
    for (const opt of selected) sign_score += _opt_support(question, opt, asc_sign, influence);
    let planet_score = 0.0;
    for (const planet of lagna_planets) {
      const supported = PLANET_LAGNA_TRAITS[planet]?.[question.key] ?? new Set<string>();
      if (selected.some((opt) => supported.has(opt))) {
        planet_score = _PLANET_LAGNA_CREDIT;
        break;
      }
    }
    const contribution = Math.min(1.0, Math.max(sign_score, planet_score));
    total += weight * contribution;
    lagna_total += weight * contribution;
    per_question[question.key] = {
      selected,
      target: "lagna",
      sign_score: pythonRound(Math.min(1.0, sign_score), 2),
      planet_score: pythonRound(planet_score, 2),
    };
  }

  if (max_weight <= 0) {
    // No additive answers scored. If the only thing answered is birth-circumstance lore (a REAL
    // answer, i.e. one that produced a chart check — pure `unknown` picks stay a full no-op and
    // fall through to the legacy null), the candidate still deserves its bounded chart check:
    // start from the same neutral baseline the rejected-only path uses and apply the multiplier ×
    // gates (delivery_ease-only profiles etc.).
    const answered_circumstance = [...CHART_CIRCUMSTANCE_KEYS].some((k) => k in circ_detail);
    if (answered_circumstance) {
      return [
        REJECTED_ONLY_NEUTRAL_SCORE * circ_mult * lagna_gate * moon_gate,
        {
          ascendant_sign: asc_sign,
          moon_sign,
          circumstances_only: true,
          appearance_physical_score: pythonRound(REJECTED_ONLY_NEUTRAL_SCORE * circ_mult * lagna_gate, 2),
          rejected_gate: has_rejections ? pythonRound(lagna_gate * moon_gate, 3) : null,
          ...circ_fields,
        },
      ];
    }
    return [null, {}];
  }
  const primary_score = (100.0 * total) / max_weight;
  // The PHYSICAL-only baseline drives the "weak sign cliché" flag (a Moon-disposition match must
  // never make a non-matching body look like a good rising-sign fit).
  const physical_primary = lagna_max_weight > 0 ? (100.0 * lagna_total) / lagna_max_weight : primary_score;
  // The DISPOSITION-only score (vs the Moon sign) — the Personality metric, exposed independently.
  const disposition_score = moon_max_weight > 0 ? pythonRound((100.0 * moon_total) / moon_max_weight, 2) : null;

  // Vol_06 multi-factor blend (PHYSICAL only): the Ascendant cross-checked against the Lagna lord,
  // lagna nakshatra lord, Navamsa Lagna lord, Moon sign and strongest planet. Disposition answers
  // are excluded from this physical cross-check.
  // delivery_type is excluded from this cross-check: Step 6 ties it to the ASCENDANT's rise
  // direction alone ("determined at the moment of birth by the Ascendant sign") — it is a birth
  // fact, not a body resemblance the Moon sign / lords could also carry.
  // The chart-scored circumstance questions are likewise excluded — like delivery_type they are
  // birth FACTS tied to the Ascendant condition itself, not body resemblances the Moon sign /
  // lords could also carry.
  const physical_clean: Record<string, string[]> = {};
  for (const q of TRAIT_QUESTIONS) {
    if (q.target === "lagna" && q.key !== DELIVERY_TYPE_KEY && !CHART_CIRCUMSTANCE_KEYS.has(q.key) && clean[q.key]?.length)
      physical_clean[q.key] = clean[q.key];
  }
  const [secondary_score, secondary_detail] = _secondary_appearance_match(natal_chart, physical_clean, influence);
  let score: number;
  if (secondary_score !== null) {
    score = primary_score * (1.0 - APPEARANCE_SECONDARY_WEIGHT) + secondary_score * APPEARANCE_SECONDARY_WEIGHT;
  } else {
    score = primary_score;
  }
  // Rejected-trait gates (absolute): physical exclusion on the rising sign × disposition exclusion
  // on the Moon sign.
  score *= lagna_gate * moon_gate;
  // Birth-circumstance × gender-parity multiplier — bounded (×0.85..×1.15 overall), never a veto,
  // exactly 1.0 when nothing relevant is answered (see _circumstance_multiplier).
  score *= circ_mult;

  // PHYSICAL-ONLY appearance score for the candidate-ranking "appearance" bucket. The Ascendant
  // body match (physical sign baseline blended with the physical modifier cross-check), gated by
  // the PHYSICAL rejection ONLY. Moon-disposition answers and the Moon-sign rejection gate are
  // deliberately EXCLUDED so the personality (Moon) signal can never leak INTO the appearance
  // bucket, nor be suppressed BY it (the two gates are no longer multiplied for this number).
  // Null when there are no physical answers — the bucket then drops out of the evidence combine
  // rather than borrowing a Moon-derived value. (The combined, Moon-inclusive `score` above is
  // kept unchanged for callers/notes that still want the blended figure.)
  // The circumstance multiplier applies here too: the birth-circumstance checks are ASCENDANT
  // conditions (drishti on the 1st, dual/fixed/vargottama lagna, gandanta windows), so they
  // belong to the appearance/rising-sign bucket, not the Moon bucket.
  const appearance_physical: number | null =
    lagna_max_weight > 0
      ? (secondary_score !== null
          ? physical_primary * (1.0 - APPEARANCE_SECONDARY_WEIGHT) + secondary_score * APPEARANCE_SECONDARY_WEIGHT
          : physical_primary) * lagna_gate * circ_mult
      : null;

  // The PHYSICAL sign-ONLY baseline (Ascendant checklist, no modifiers) — surfaced so the report can
  // show when a chart fails the sign cliché yet remains valid (the Johnny-Depp / Cancer case).
  const sign_only_weak = physical_primary < SIGN_ONLY_WEAK_BELOW;
  const appearance_note = sign_only_weak
    ? `${SIGN_NAMES[asc_sign]} sign-only appearance match is weak (${Math.round(physical_primary)}%), but ` +
      `the modifier-based blend lands at ${Math.round(score)}%. A weak sign checklist does not ` +
      `invalidate the lagna — appearance is secondary evidence; the dated events and the lagna ` +
      `nakshatra/lord/aspect modifiers carry the rectification.`
    : null;

  return [
    score,
    {
      ascendant_sign: asc_sign,
      moon_sign,
      lagna_planets,
      answered: Object.keys(per_question).length,
      per_question,
      primary_score: pythonRound(primary_score, 2),
      physical_primary_score: pythonRound(physical_primary, 2),
      appearance_physical_score: appearance_physical !== null ? pythonRound(appearance_physical, 2) : null,
      disposition_score, // 0-100 vs the Moon sign (Personality metric), or null
      secondary_score: secondary_score !== null ? pythonRound(secondary_score, 2) : null,
      secondary_factors: secondary_detail,
      // Modifier-based summary fields: the raw sign-only baseline, the weak flag, and the note.
      sign_only_score: pythonRound(physical_primary, 2),
      modifier_score: secondary_score !== null ? pythonRound(secondary_score, 2) : null,
      sign_only_weak,
      appearance_note,
      rejected_gate: has_rejections ? pythonRound(lagna_gate * moon_gate, 3) : null,
      rejected: has_rejections ? normalize_trait_answers(rejected, age_years) : null,
      ...circ_fields,
    },
  ];
}
